/* ============================================
   LaneBooker Admin — legacy static UI, API-backed.
   Derived byte-for-byte from legacy/the-alley-os/admin/admin.js.
   The ONLY change is the data layer: localStorage → REST API.
   ============================================ */

import { initSentry, tagAdminTenant, tagAdminUser } from './sentry';
// Initialize Sentry as early as possible so module-load errors are captured.
initSentry();

import { ensureAuth } from './auth';
import { tenantApi, ApiError, authApi, type TenantMembership } from './api';
import { resolveTenantSlug } from './tenant';
import { mountDocs } from './docs';
import { installUpgradeModalInterceptor, mountUpgradeModal } from './upgrade-modal';

// =============================================================================
// Install the 402 plan_required interceptor + mount the UpgradeModal BEFORE
// auth runs. We do this at module init (not inside the post-auth bootstrap)
// so the modal is available even on early API calls — e.g. the initial
// `ensureAuth()` fetch or any tenant-info bootstrap call that fires before
// the dashboard finishes wiring. See apps/admin/src/upgrade-modal.ts for the
// full design rationale.
// =============================================================================
installUpgradeModalInterceptor();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountUpgradeModal(), { once: true });
} else {
  mountUpgradeModal();
}

// =============================================================================
// Wire logout button — runs once at module init, before any route logic.
// This ensures the button works from any path (picker, tenant, error page).
// =============================================================================

function wireLogout() {
  const btn = document.getElementById('btn-logout');
  if (!btn) return;
  btn.addEventListener('click', async (e) => {
    e.preventDefault();
    try {
      await authApi.logout();
    } catch {
      // Ignore — we redirect regardless so a stuck session can't trap the user.
    }
    window.location.href = '/login.html';
  });
}
wireLogout();

// =============================================================================
// Types — narrow API payloads into the shapes the legacy UI expects.
// =============================================================================

interface ApiActivity {
  id: string;
  slug: string;
  label: string;
  category: string;
  duration_minutes: number | null;
  enabled: number;
  icon?: string | null;
  image_url?: string | null;
}

interface ApiResource {
  id: string;
  activity_id: string;
  number: number;
  label: string | null;
  status: string;
}

interface ApiAddon {
  id: string;
  slug: string;
  label: string;
  price_cents: number;
  per_person: number;
  auto_add_per_person?: number;
  icon: string | null;
  category: string | null;
  enabled: number;
}

interface ApiPackage {
  id: string;
  slug: string;
  label: string;
  description: string | null;
  price_cents: number;
  enabled: number;
}

interface ApiBooking {
  id: string;
  activity_id: string;
  start_at: number; // UTC epoch seconds
  end_at: number;
  booking_type: 'online' | 'phone' | 'walkin' | 'blocked';
  payment_status: 'pending' | 'paid' | 'partial' | 'refunded';
  status?: 'confirmed' | 'cancelled' | 'no_show' | 'completed';
  source: string | null;
  notes: string | null;
  total_cents: number;
  booker_name: string;
  booker_phone: string | null;
  booker_email: string | null;
  party_size: number | null;
  stripe_payment_intent?: string | null;
  refunded_at?: number | null;
  refund_id?: string | null;
  customer_id?: string | null;
}

interface BookingDetail {
  booking: ApiBooking;
  resources: string[];
  addons: Array<{ addon_id: string; qty: number; price_cents_snapshot: number }>;
  packages: Array<{ package_id: string; price_cents_snapshot: number }>;
}

interface EnrichedCustomer {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  joined_at: number | null;
  tags: string[];
  staff_notes: string | null;
  total_visits: number;
  lifetime_value_cents: number;
  last_visit_at: number | null;
  avg_spend_cents: number;
  frequency: string;
  is_member: number;
  new_this_month: number;
  last_activity: string | null;
}

// =============================================================================
// Bootstrap: verify auth + resolve tenant, then initialise.
// =============================================================================

const tenantInfo = resolveTenantSlug();
if (tenantInfo) tagAdminTenant(tenantInfo.slug);
if (!tenantInfo) {
  // No slug — ensureAuth() will redirect to single-tenant home or render picker.
  // Render picker if user has multiple memberships.
  void ensureAuth().then((me) => {
    tagAdminUser({ id: me.user.id, email: me.user.email });
    if (me.memberships.length > 1) renderTenantPicker(me.memberships, me);
    // else: ensureAuth has already triggered a redirect (incl. platform admins
    // bouncing to /platform.html).
  });
} else {
  // Slug is present — gate on auth, then boot the full app.
  void ensureAuth().then((me) => {
    tagAdminUser({ id: me.user.id, email: me.user.email });
    // Apply the user's saved admin theme as early as possible (before bootApp's
    // async data fetches paint). localStorage wins for same-device, then server.
    applyAdminTheme(me.user.admin_theme ?? null);
    // Hide the Setup tab for staff-role members; owner/admin see it.
    const membership = me.memberships.find((m) => m.tenant_slug === tenantInfo.slug);
    if (membership && membership.role === 'staff') {
      const tab = document.getElementById('tab-setup');
      if (tab) tab.style.display = 'none';
    }
    bootApp(tenantInfo.slug, me);
  });
}

const THEME_KEYS = [
  'light-blue',
  'light-violet',
  'light-emerald',
  'dark-blue',
  'dark-violet',
  'dark-emerald',
] as const;
type ThemeKey = (typeof THEME_KEYS)[number];
const DEFAULT_THEME: ThemeKey = 'light-blue';

function applyAdminTheme(serverTheme: string | null): ThemeKey {
  const stored = (localStorage.getItem('lanebooker_admin_theme') ?? '') as ThemeKey;
  const pick: ThemeKey = (THEME_KEYS as readonly string[]).includes(stored)
    ? stored
    : (THEME_KEYS as readonly string[]).includes(serverTheme ?? '')
      ? (serverTheme as ThemeKey)
      : DEFAULT_THEME;
  previewAdminTheme(pick);
  localStorage.setItem('lanebooker_admin_theme', pick);
  return pick;
}

/** Toggle body class only — no persistence. Used while the user is previewing
 *  themes before hitting Save. */
function previewAdminTheme(theme: string): void {
  THEME_KEYS.forEach((t) => document.body.classList.remove(`theme-${t}`));
  if ((THEME_KEYS as readonly string[]).includes(theme)) {
    document.body.classList.add(`theme-${theme}`);
  }
}

function renderTenantPicker(memberships: TenantMembership[], me?: import('./api').Me) {
  // TODO: when /api/auth/me returns `address` per membership, render it under
  // the slug subtitle. For now we only have tenant_id/slug/name/role.
  const roleColors: Record<string, { bg: string; fg: string }> = {
    owner: { bg: '#fef3c7', fg: '#92400e' },
    admin: { bg: '#dbeafe', fg: '#1e40af' },
    staff: { bg: '#e0e7ff', fg: '#3730a3' },
    support: { bg: '#f3f4f6', fg: '#374151' },
  };

  // User-facing role labels — keep DB value 'staff' but show 'Front Desk' to humans.
  const roleLabels: Record<string, string> = {
    owner: 'Owner',
    admin: 'Admin',
    staff: 'Front Desk',
    support: 'Support',
  };
  const cards = memberships
    .map((m) => {
      const role = roleColors[m.role] ?? roleColors.staff;
      const label = roleLabels[m.role] ?? m.role;
      return `
        <div class="tp-card">
          <div class="tp-card-body">
            <div class="tp-card-head">
              <h2 class="tp-name">${escapeHtml(m.tenant_name)}</h2>
              <span class="tp-role" style="background:${role.bg};color:${role.fg}">${escapeHtml(label)}</span>
            </div>
            <div class="tp-slug">${escapeHtml(m.tenant_slug)}</div>
          </div>
          <a class="tp-cta" href="/t/${encodeURIComponent(m.tenant_slug)}/">Open dashboard <span aria-hidden="true">&rarr;</span></a>
        </div>`;
    })
    .join('');

  const isPlatformAdmin = me?.user?.platform_admin === 1;
  const platformLink = isPlatformAdmin
    ? `<a class="tp-platform-link" id="picker-platform-link" href="/platform.html">Platform</a>`
    : '';

  document.body.innerHTML = `
    <style>
      .tp-wrap { min-height: 100vh; padding: 0 0 4rem; background: #f4f6fb; font-family: Inter, system-ui, -apple-system, sans-serif; }
      .tp-topbar { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.5rem; background: #fff; border-bottom: 1px solid #e2e6ed; box-shadow: 0 1px 2px rgba(15,23,42,0.04); }
      .tp-topbar-logo { display: flex; align-items: center; gap: 0.5rem; font-size: 1.05rem; font-weight: 700; color: #0f172a; }
      .tp-topbar-right { display: flex; align-items: center; gap: 1rem; }
      .tp-platform-link { display: inline-block; padding: 0.45rem 0.9rem; background: #0f172a; color: #fff; border-radius: 6px; font-size: 0.85rem; font-weight: 600; text-decoration: none; transition: background 140ms ease; }
      .tp-platform-link:hover { background: #1e293b; }
      .tp-inner { max-width: 1040px; margin: 0 auto; padding: 4rem 1.5rem 0; }
      .tp-header { text-align: center; margin-bottom: 2.25rem; }
      .tp-title { margin: 0 0 0.5rem 0; font-size: 1.75rem; font-weight: 700; color: #0f172a; letter-spacing: -0.01em; }
      .tp-sub { margin: 0; color: #64748b; font-size: 0.95rem; }
      .tp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1rem; }
      .tp-card { display: flex; flex-direction: column; background: #fff; border: 1px solid #e2e6ed; border-radius: 14px; box-shadow: 0 1px 2px rgba(15,23,42,0.04); overflow: hidden; transition: box-shadow 160ms ease, transform 160ms ease, border-color 160ms ease; }
      .tp-card:hover { box-shadow: 0 12px 28px rgba(15,23,42,0.10); transform: translateY(-2px); border-color: #c7d2fe; }
      .tp-card-body { padding: 1.25rem 1.25rem 1rem; flex: 1; }
      .tp-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.35rem; }
      .tp-name { margin: 0; font-size: 1.1rem; font-weight: 650; color: #0f172a; line-height: 1.3; }
      .tp-role { display: inline-block; padding: 0.18rem 0.55rem; font-size: 0.68rem; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; border-radius: 999px; flex-shrink: 0; }
      .tp-slug { font-size: 0.82rem; color: #64748b; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
      .tp-cta { display: flex; align-items: center; justify-content: space-between; padding: 0.85rem 1.25rem; background: #f8fafc; border-top: 1px solid #eef1f6; color: #2563eb; font-weight: 600; font-size: 0.9rem; text-decoration: none; transition: background 140ms ease; }
      .tp-cta:hover { background: #eef4ff; }
      .tp-footer { text-align: center; margin-top: 2rem; }
      .tp-signout { color: #64748b; font-size: 0.85rem; text-decoration: none; }
      .tp-signout:hover { color: #2563eb; }
    </style>
    <div class="tp-wrap">
      <header class="tp-topbar">
        <div class="tp-topbar-logo">
          <span>&#127923;</span>
          <span>LaneBooker</span>
        </div>
        <div class="tp-topbar-right">
          ${platformLink}
        </div>
      </header>
      <div class="tp-inner">
        <div class="tp-header">
          <h1 class="tp-title">Choose a location</h1>
          <p class="tp-sub">You manage ${memberships.length} workspaces. Pick one to open its dashboard.</p>
        </div>
        <div class="tp-grid">${cards}</div>
        <div class="tp-footer">
          <a class="tp-signout" id="picker-signout" href="#signout">Sign out</a>
        </div>
      </div>
    </div>`;

  // Wire the Sign out link to call the logout API first.
  const signoutLink = document.getElementById('picker-signout');
  if (signoutLink) {
    signoutLink.addEventListener('click', async (e) => {
      e.preventDefault();
      try {
        await authApi.logout();
      } catch {
        // Ignore — we redirect regardless.
      }
      window.location.href = '/login.html';
    });
  }
}

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

// =============================================================================
// Core app — closure scope, matches legacy admin.js exactly
// =============================================================================

async function bootApp(slug: string, me: import('./api').Me) {
  const api = tenantApi(slug);

  // Logout button is wired at module init by wireLogout() — no need to bind here.

  // Track the current user's role for this tenant — needed to gate the Venue
  // details panel (owner/admin only) inside Setup → Profile.
  const membership = me.memberships.find((m) => m.tenant_slug === slug);
  const tenantRole = membership?.role ?? 'staff';
  const canEditVenue = tenantRole === 'owner' || tenantRole === 'admin' || me.user.platform_admin === 1;
  const isPlatformAdmin = me.user.platform_admin === 1;

  // Persistent past_due billing banner. Surfaced server-side via
  // /api/t/:slug/info (returns tenants.billing_status). Non-blocking — admin
  // can still operate the dashboard; CTA opens the Stripe billing portal.
  // Defined here, called after `tenant` is populated below.
  function mountPastDueBanner(billingStatus: string | null | undefined, tenantId: string | undefined): void {
    if (billingStatus !== 'past_due') return;
    if (document.getElementById('lb-past-due-banner')) return;
    const header = document.querySelector('header.top-bar');
    if (!header) return;
    const banner = document.createElement('div');
    banner.id = 'lb-past-due-banner';
    banner.setAttribute('data-testid', 'admin-past-due-banner');
    banner.setAttribute('role', 'status');
    banner.style.cssText = [
      'padding:10px 20px',
      'background:#FEF3C7',
      'color:#92400E',
      'font-size:0.875rem',
      'font-weight:500',
      'border-bottom:1px solid #FDE68A',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'gap:0.75rem',
      'flex-wrap:wrap',
    ].join(';');
    const msg = document.createElement('span');
    msg.textContent = 'Your billing is past due. Update payment to avoid suspension.';
    banner.appendChild(msg);
    const cta = document.createElement('a');
    cta.id = 'lb-past-due-cta';
    cta.setAttribute('data-testid', 'admin-past-due-cta');
    cta.href = '#';
    cta.textContent = 'Update payment →';
    cta.style.cssText = 'color:#92400E;font-weight:700;text-decoration:underline;cursor:pointer';
    cta.addEventListener('click', async (e) => {
      e.preventDefault();
      const fallback = `/t/${encodeURIComponent(slug)}/settings/billing`;
      if (!tenantId) { window.location.href = fallback; return; }
      try {
        // Reuse the existing /api/stripe/portal endpoint for a one-tap link.
        const r = await fetch('/api/stripe/portal', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tenant_id: tenantId }),
        });
        if (r.ok) {
          const j = (await r.json()) as { url?: string };
          if (j.url) { window.location.href = j.url; return; }
        }
      } catch {
        /* fall through to the in-app billing page */
      }
      window.location.href = fallback;
    });
    banner.appendChild(cta);
    header.insertAdjacentElement('afterend', banner);
  }

  // Platform admin entry point — inject a "Platform" link in the top-bar-right
  // (visible on every tenant dashboard) that opens the platform admin dashboard.
  // The "+ New Client" wizard entry-point lives on the /platform page itself.
  if (isPlatformAdmin) {
    const right = document.querySelector('.top-bar-right');
    if (right && !document.getElementById('platform-top-link')) {
      const link = document.createElement('a');
      link.id = 'platform-top-link';
      link.href = '/platform.html';
      link.textContent = 'Platform';
      link.title = 'Open the platform admin dashboard';
      link.style.display = 'inline-flex';
      link.style.alignItems = 'center';
      link.style.padding = '0.4rem 0.8rem';
      link.style.background = '#0f172a';
      link.style.color = '#fff';
      link.style.borderRadius = '6px';
      link.style.fontSize = '0.82rem';
      link.style.fontWeight = '600';
      link.style.textDecoration = 'none';
      link.style.marginRight = '0.5rem';
      // Insert before the Add Booking button so it sits to the left of it.
      right.insertBefore(link, right.firstChild);
    }
  }

  // Top-bar location switcher — only shown when the user has multiple memberships.
  if (me.memberships.length > 1) {
    mountLocationSwitcher(slug, me.memberships);
  }

  // TODO: Implement the multi-tenant location switcher UI in a follow-up.
  // Stubbed for now so /t/{slug}/ pages don't hit a ReferenceError when a user
  // has memberships in more than one tenant.
  function mountLocationSwitcher(_slug: string, _memberships: TenantMembership[]): void {
    /* no-op stub */
  }

  // Load tenant basics + bookable data up front so the UI can render.
  let tenant: Record<string, unknown> = {};
  let activities: ApiActivity[] = [];
  let resources: ApiResource[] = [];
  let addons: ApiAddon[] = [];
  let packages: ApiPackage[] = [];
  interface ScheduleEvent {
    id: string;
    name: string;
    recurrence: string | null;
    theme_color: string | null;
    enabled: 0 | 1;
  }
  let scheduleEvents: ScheduleEvent[] = [];

  interface PricingRuleRow {
    id: string;
    activity_id: string;
    day_of_week: number | null;
    start_time: string | null;
    end_time: string | null;
    rate_type: 'per_hour' | 'per_person_per_hour' | 'per_person_flat' | 'per_round' | 'flat';
    rate_cents: number;
    hour_index: number | null;
    min_party_size: number | null;
    max_party_size: number | null;
    label: string | null;
  }
  let pricingRules: PricingRuleRow[] = [];

  try {
    const [info, act, res, add, pkg, ev, pr] = await Promise.all([
      api.info(),
      api.activities(),
      api.resources(),
      api.addons(),
      api.packages(),
      api.events().catch(() => ({ events: [] })),
      api.pricing().catch(() => ({ rules: [] })),
    ]);
    tenant = info.tenant;
    // Mount past_due banner now that tenant is loaded.
    mountPastDueBanner(
      (tenant as { billing_status?: string | null }).billing_status ?? null,
      (tenant as { id?: string }).id,
    );
    activities = (act.activities as unknown as ApiActivity[]).filter((a) => a.enabled);
    resources = (res.resources as unknown as ApiResource[]).filter((r) => r.status === 'active');
    addons = (add.addons as unknown as ApiAddon[]).filter((a) => a.enabled);
    packages = (pkg.packages as unknown as ApiPackage[]).filter((p) => p.enabled);
    scheduleEvents = ((ev.events as unknown as ScheduleEvent[]) || []).filter((e) => e.enabled === 1);
    pricingRules = (pr.rules as unknown as PricingRuleRow[]) ?? [];
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = '/login.html';
      return;
    }
    console.error('[admin] failed to load tenant data', err);
    alert('Could not load workspace data. Check the console and try again.');
    return;
  }

  // Legacy used numeric lane ids 1..16. The API returns opaque UUIDs. We build
  // an index keyed by a synthetic "lane id" (activity slug + number) so the
  // legacy grid logic works unchanged.
  interface Lane {
    id: string; // resource UUID
    label: string;
    type: '5-pin' | '10-pin' | 'golf-sim' | 'laser-tag' | 'other';
    number: number;
    activityId: string;
    activitySlug: string;
  }

  const activityById = new Map(activities.map((a) => [a.id, a]));
  const activityBySlug = new Map(activities.map((a) => [a.slug, a]));

  function laneTypeForActivity(a: ApiActivity): Lane['type'] {
    if (a.slug.includes('5-pin') || a.slug === 'bowling-5pin') return '5-pin';
    if (a.slug.includes('10-pin') || a.slug === 'bowling-10pin') return '10-pin';
    if (a.slug.includes('golf')) return 'golf-sim';
    if (a.slug.includes('laser')) return 'laser-tag';
    return 'other';
  }

  // Build the lane list ordered exactly like the legacy (all 5-pin first, then 10-pin, each 1..N).
  const laneList: Lane[] = [];
  const fivePinAct = activityBySlug.get('bowling-5pin') || activityBySlug.get('5-pin');
  const tenPinAct = activityBySlug.get('bowling-10pin') || activityBySlug.get('10-pin');
  if (fivePinAct) {
    resources
      .filter((r) => r.activity_id === fivePinAct.id)
      .sort((a, b) => a.number - b.number)
      .forEach((r) =>
        laneList.push({
          id: r.id,
          label: r.label || `5-Pin Lane ${r.number}`,
          type: '5-pin',
          number: r.number,
          activityId: fivePinAct.id,
          activitySlug: fivePinAct.slug,
        }),
      );
  }
  if (tenPinAct) {
    resources
      .filter((r) => r.activity_id === tenPinAct.id)
      .sort((a, b) => a.number - b.number)
      .forEach((r) =>
        laneList.push({
          id: r.id,
          label: r.label || `10-Pin Lane ${r.number}`,
          type: '10-pin',
          number: r.number,
          activityId: tenPinAct.id,
          activitySlug: tenPinAct.slug,
        }),
      );
  }

  const laneById = new Map(laneList.map((l) => [l.id, l]));
  // Use real resource count from D1. Never fall back to a fake constant —
  // tenants with 0 lanes (e.g. Demo) should correctly show 0/0, not 16/16.
  const totalLanes = laneList.length;

  // ---- Date / hour constants (preserve legacy behaviour) ----
  const HOURS: number[] = [];
  for (let h = 10; h <= 23; h++) HOURS.push(h);

  // ---- State ----
  let currentDate = todayStr();
  let editingBookingId: string | null = null;

  // ---- Date helpers (verbatim from legacy) ----
  function todayStr() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  function formatDateDisplay(dateStr: string) {
    const d = new Date(dateStr + 'T12:00:00');
    const opts: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return d.toLocaleDateString('en-US', opts);
  }
  function formatHour(h: number) {
    if (h === 0 || h === 24) return '12 AM';
    if (h === 12) return '12 PM';
    if (h < 12) return h + ' AM';
    return h - 12 + ' PM';
  }
  /** Format minutes-since-midnight as "4:30 PM" / "10 AM". */
  function formatHM(mins: number) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (m === 0) return formatHour(h);
    const displayH = h === 0 || h === 24 ? 12 : h <= 12 ? h : h - 12;
    const suffix = h < 12 || h === 24 ? 'AM' : 'PM';
    return `${displayH}:${String(m).padStart(2, '0')} ${suffix}`;
  }
  function shiftDate(dateStr: string, days: number) {
    const d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  function getDayOfWeek(dateStr: string) {
    return new Date(dateStr + 'T12:00:00').getDay();
  }

  /** Convert a local date (YYYY-MM-DD) + hour into an epoch, in the browser's tz. */
  function localHourToEpoch(date: string, hour: number): number {
    const [y, m, day] = date.split('-').map(Number);
    const dt = new Date(y, m - 1, day, hour, 0, 0);
    return Math.floor(dt.getTime() / 1000);
  }
  /** Convert a local date (YYYY-MM-DD) + minutes-since-midnight into an epoch. */
  function localMinutesToEpoch(date: string, mins: number): number {
    const [y, m, day] = date.split('-').map(Number);
    const dt = new Date(y, m - 1, day, Math.floor(mins / 60), mins % 60, 0);
    return Math.floor(dt.getTime() / 1000);
  }

  // ---- Booking fetch + grid build ----
  async function fetchBookingsForDay(date: string): Promise<ApiBooking[]> {
    const [y, m, d] = date.split('-').map(Number);
    const fromEpoch = Math.floor(new Date(y, m - 1, d, 0, 0, 0).getTime() / 1000);
    const toEpoch = Math.floor(new Date(y, m - 1, d + 1, 0, 0, 0).getTime() / 1000);
    const fromIso = new Date(fromEpoch * 1000).toISOString();
    const toIso = new Date(toEpoch * 1000).toISOString();
    const { bookings } = await api.listBookings(fromIso, toIso);
    return bookings as unknown as ApiBooking[];
  }

  async function buildGrid() {
    const grid = document.getElementById('schedule-grid')!;
    grid.innerHTML = '<div style="padding:2rem;color:#6b7280;font-size:0.9rem">Loading…</div>';
    let raw: ApiBooking[] = [];
    try {
      raw = await fetchBookingsForDay(currentDate);
    } catch (err) {
      console.error(err);
      grid.innerHTML =
        '<div style="padding:2rem;color:#dc2626;font-size:0.9rem">Failed to load bookings.</div>';
      return;
    }
    grid.innerHTML = '';

    // Hydrate: compute start hour + duration in local tz for each booking.
    const bookingsDay = raw
      .map((b) => {
        const start = new Date(b.start_at * 1000);
        const end = new Date(b.end_at * 1000);
        const startHour = start.getHours();
        const startMinute = start.getMinutes();
        const durHours = Math.max(1, Math.round((b.end_at - b.start_at) / 3600));
        const dateLocal = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
        return { b, startHour, startMinute, durHours, dateLocal, end };
      })
      .filter((row) => row.dateLocal === currentDate);

    // Fetch resource assignments for each booking so we can render lanes.
    // (The list endpoint doesn't return booking_resources; we need per-booking.)
    // Batch with Promise.all but keep the total small — <=200 bookings per day in practice.
    const details = await Promise.all(
      bookingsDay.map((r) =>
        api
          .getBooking(r.b.id)
          .then((d) => ({ ...r, resourceIds: d.resources as string[] }))
          .catch(() => ({ ...r, resourceIds: [] as string[] })),
      ),
    );

    // Find active event for the current day (first matching event wins)
    const dow = getDayOfWeek(currentDate);
    let activeEvent: {
      name: string;
      color: string;
      startHour: number;
      endHour: number;
    } | null = null;
    for (const ev of scheduleEvents) {
      if (!ev.recurrence) continue;
      try {
        const rec = JSON.parse(ev.recurrence) as { days: number[]; start: string; end: string };
        if (!Array.isArray(rec.days) || !rec.days.includes(dow)) continue;
        const [sh] = rec.start.split(':').map((n) => Number(n));
        const [eh, em] = rec.end.split(':').map((n) => Number(n));
        activeEvent = {
          name: ev.name,
          color: ev.theme_color || '#a855f7',
          startHour: sh,
          endHour: em && em > 0 ? eh : eh - 1, // end is exclusive-ish; 23:30 -> paint through 23
        };
        break;
      } catch {
        /* skip bad recurrence json */
      }
    }
    const nowHour = new Date().getHours();
    const isToday = currentDate === todayStr();

    // Header row
    const corner = el('div', 'grid-header corner', '');
    grid.appendChild(corner);
    HOURS.forEach((h) => {
      const hdr = el('div', 'grid-header', formatHour(h));
      if (isToday && h === nowHour) hdr.style.background = '#eff6ff';
      grid.appendChild(hdr);
    });

    // Lookup: laneId -> hour -> detail
    const laneMap = new Map<string, Map<number, typeof details[number]>>();
    laneList.forEach((l) => laneMap.set(l.id, new Map()));
    details.forEach((row) => {
      row.resourceIds.forEach((rid) => {
        const bucket = laneMap.get(rid);
        if (!bucket) return;
        for (let h = row.startHour; h < row.startHour + row.durHours; h++) {
          bucket.set(h, row);
        }
      });
    });

    // 5-Pin section
    const sec5 = el('div', 'grid-section-label', '5-Pin Bowling');
    grid.appendChild(sec5);
    laneList
      .filter((l) => l.type === '5-pin')
      .forEach((lane) => renderLaneRow(grid, lane, laneMap.get(lane.id)!, activeEvent, nowHour, isToday));

    // 10-Pin section
    const sec10 = el('div', 'grid-section-label', '10-Pin Bowling');
    grid.appendChild(sec10);
    laneList
      .filter((l) => l.type === '10-pin')
      .forEach((lane) => renderLaneRow(grid, lane, laneMap.get(lane.id)!, activeEvent, nowHour, isToday));

    updateSummary(details);
  }

  function renderLaneRow(
    grid: HTMLElement,
    lane: Lane,
    hourMap: Map<number, { b: ApiBooking; startHour: number; durHours: number }>,
    activeEvent: { name: string; color: string; startHour: number; endHour: number } | null,
    nowHour: number,
    isToday: boolean,
  ) {
    const label = el('div', 'grid-lane-label', '');
    const badge = el(
      'span',
      `lane-type-badge ${lane.type === '5-pin' ? 'five' : 'ten'}`,
      lane.type === '5-pin' ? '5' : '10',
    );
    label.appendChild(badge);
    label.appendChild(document.createTextNode(' Lane ' + lane.number));
    grid.appendChild(label);

    const rendered = new Set<string>();

    HOURS.forEach((h) => {
      const cell = el('div', 'grid-cell', '');
      if (isToday && h === nowHour) cell.classList.add('now-col');

      const inEventWindow = !!(activeEvent && h >= activeEvent.startHour && h <= activeEvent.endHour);

      // Bookings always win over event overlay. If a booking exists in an event
      // window we still paint the booking, just with a thin event-color border
      // so staff can tell it's during theme hours.
      const row = hourMap.get(h);
      if (row && !rendered.has(row.b.id + '_' + lane.id)) {
        rendered.add(row.b.id + '_' + lane.id);
        const spanCols = row.durHours;
        const typeClass = getBookingClass(row.b);
        cell.classList.add(typeClass);
        if (spanCols > 1) cell.classList.add('span-start');
        if (inEventWindow && activeEvent) {
          cell.style.boxShadow = `inset 3px 0 0 ${activeEvent.color}`;
        }
        cell.innerHTML = buildCellContent(row.b, row.durHours, true);
        cell.addEventListener('click', () => openBookingDetail(row.b.id));
        grid.appendChild(cell);
        for (let s = 1; s < spanCols; s++) {
          const contIdx = HOURS.indexOf(h) + s;
          if (contIdx >= HOURS.length) break;
          const cont = el('div', `grid-cell ${typeClass}`, '');
          if (isToday && HOURS[contIdx] === nowHour) cont.classList.add('now-col');
          cont.classList.add(s === spanCols - 1 ? 'span-end' : 'span-mid');
          cont.addEventListener('click', () => openBookingDetail(row.b.id));
          grid.appendChild(cont);
        }
        return;
      } else if (row && rendered.has(row.b.id + '_' + lane.id)) {
        return;
      }

      // No booking here — paint event overlay if in window, else available.
      if (inEventWindow && activeEvent) {
        cell.classList.add('rocknbowl');
        cell.style.background = hexWithAlpha(activeEvent.color, 0.2);
        if (h === activeEvent.startHour) {
          cell.innerHTML = `<div class="cell-rocknbowl-label" style="color:${esc(activeEvent.color)}">${esc(activeEvent.name)}</div>`;
        }
        cell.addEventListener('click', () => openNewBooking(lane, h));
        grid.appendChild(cell);
        return;
      }

      cell.classList.add('available');
      cell.addEventListener('click', () => openNewBooking(lane, h));
      grid.appendChild(cell);
    });
  }

  function getBookingClass(b: ApiBooking) {
    if (b.booking_type === 'blocked') return 'blocked';
    if (b.booking_type === 'online') return 'booked-online';
    return 'booked-staff';
  }

  function buildCellContent(b: ApiBooking, duration: number, showIcons: boolean) {
    // Half-hour starts are rendered in the floor-hour column with a badge so
    // staff can tell "4:30 PM start" from "4:00 PM start" at a glance. The
    // stored start_at remains exact to the minute.
    const startDate = new Date(b.start_at * 1000);
    const startsHalf = startDate.getMinutes() === 30;
    let html = `<div class="cell-content">`;
    html += `<div class="cell-name">${esc(b.booker_name)}</div>`;
    if (startsHalf) {
      html += `<div class="cell-half-badge">${formatHM(startDate.getHours() * 60 + 30)} start</div>`;
    }
    html += `<div class="cell-meta">${b.party_size ?? 0}p &middot; ${duration}hr</div>`;
    if (showIcons) {
      const icons: string[] = [];
      if (b.payment_status === 'paid') icons.push('✅');
      else if (b.payment_status === 'partial') icons.push('💰');
      else icons.push('⏳');
      html += `<div class="cell-icons">${icons.map((i) => `<span class="cell-icon">${i}</span>`).join('')}</div>`;
    }
    html += `</div>`;
    return html;
  }

  // ---- Summary sidebar ----
  function updateSummary(
    details: Array<{ b: ApiBooking; startHour: number; startMinute?: number; durHours: number; resourceIds: string[] }>,
  ) {
    const today = todayStr();
    const nowH = new Date().getHours();

    document.getElementById('summary-date')!.textContent = formatDateDisplay(currentDate);
    document.getElementById('stat-bookings')!.textContent = String(details.length);

    const revenueCents = details.reduce((sum, r) => sum + (r.b.total_cents || 0), 0);
    document.getElementById('stat-revenue')!.textContent = '$' + (revenueCents / 100).toFixed(2);

    if (currentDate === today) {
      const busyLanes = new Set<string>();
      details.forEach((r) => {
        if (nowH >= r.startHour && nowH < r.startHour + r.durHours) {
          r.resourceIds.forEach((rid) => busyLanes.add(rid));
        }
      });
      document.getElementById('stat-available')!.textContent = `${totalLanes - busyLanes.size}/${totalLanes}`;
    } else {
      document.getElementById('stat-available')!.textContent = '--';
    }

    // Next available slot
    let nextSlot = '--';
    const startSearch = currentDate === today ? Math.max(nowH, 10) : 10;
    for (let h = startSearch; h <= 23; h++) {
      const busy = new Set<string>();
      details.forEach((r) => {
        if (h >= r.startHour && h < r.startHour + r.durHours) {
          r.resourceIds.forEach((rid) => busy.add(rid));
        }
      });
      if (busy.size < totalLanes) {
        nextSlot = formatHour(h);
        break;
      }
    }
    document.getElementById('stat-next-slot')!.textContent = nextSlot;

    const upList = document.getElementById('upcoming-list')!;
    upList.innerHTML = '';
    const upcoming = details
      .filter((r) => r.startHour >= (currentDate === today ? nowH : 10))
      .sort((a, b) => a.startHour - b.startHour)
      .slice(0, 5);

    if (upcoming.length === 0) {
      upList.innerHTML =
        '<div class="upcoming-item"><span class="up-name" style="color:var(--text-muted)">No upcoming bookings</span></div>';
    } else {
      upcoming.forEach((r) => {
        const item = el('div', 'upcoming-item', '');
        const mins = r.startHour * 60 + (r.startMinute ?? 0);
        item.innerHTML = `<span class="up-name">${esc(r.b.booker_name)}</span><span class="up-time">${formatHM(mins)}</span>`;
        item.style.cursor = 'pointer';
        item.addEventListener('click', () => openBookingDetail(r.b.id));
        upList.appendChild(item);
      });
    }
  }

  // ---- Modal helpers ----
  function openModal(id: string) {
    document.getElementById(id)!.classList.add('open');
  }
  function closeModal(id: string) {
    document.getElementById(id)!.classList.remove('open');
  }

  // ---- Form building ----
  function buildTimeOptions() {
    const sel = document.getElementById('f-time') as HTMLSelectElement;
    sel.innerHTML = '<option value="">Select...</option>';
    // Value is minutes-since-midnight. Emit HH:00 and HH:30 for each hour so
    // staff can book half-hour start times (e.g. 4:30 PM) while duration stays
    // in whole-hour increments.
    HOURS.forEach((h) => {
      for (const m of [0, 30]) {
        const mins = h * 60 + m;
        const opt = document.createElement('option');
        opt.value = String(mins);
        opt.textContent = formatHM(mins);
        sel.appendChild(opt);
      }
    });
  }

  function buildActivityOptions() {
    const sel = document.getElementById('f-activity') as HTMLSelectElement;
    sel.innerHTML = '<option value="">Select...</option>';
    activities.forEach((a) => {
      const opt = document.createElement('option');
      opt.value = a.slug;
      opt.textContent = a.label;
      sel.appendChild(opt);
    });
  }

  function buildLanePicker(activitySlug: string) {
    const picker = document.getElementById('lane-picker')!;
    picker.innerHTML = '';
    let filtered: Lane[] = laneList;
    if (activitySlug === '5-pin' || activitySlug === 'bowling-5pin') {
      filtered = laneList.filter((l) => l.type === '5-pin');
    } else if (activitySlug === '10-pin' || activitySlug === 'bowling-10pin') {
      filtered = laneList.filter((l) => l.type === '10-pin');
    } else if (activitySlug && !activitySlug.includes('pin')) {
      picker.innerHTML =
        '<span style="font-size:0.8rem;color:var(--text-muted)">No lane selection needed</span>';
      return;
    }
    filtered.forEach((l) => {
      const chip = el('div', 'lane-chip', l.label);
      chip.dataset.id = l.id;
      chip.addEventListener('click', () => {
        chip.classList.toggle('selected');
        recalcPrice();
      });
      picker.appendChild(chip);
    });
  }

  function buildAddons() {
    const container = document.getElementById('addons-grid')!;
    container.innerHTML = '';
    addons.forEach((a) => {
      const item = el('div', 'addon-item', '');
      item.dataset.id = a.id;
      const price = a.price_cents / 100;
      const unit = a.per_person ? '/person' : '';
      const priceLabel = a.price_cents === 0 ? 'Free' : `$${price.toFixed(2)}${unit ? ' ' + unit : ''}`;
      const icon = a.icon || '🍽️';
      item.innerHTML = `
        <input type="checkbox">
        <span class="cell-icon">${icon}</span>
        <span class="addon-name">${esc(a.label)}</span>
        <input type="number" class="addon-qty" value="1" min="1" max="50">
        <span class="addon-price">${priceLabel}</span>
      `;
      const cb = item.querySelector('input[type="checkbox"]') as HTMLInputElement;
      const qtyInput = item.querySelector('.addon-qty') as HTMLInputElement;
      cb.addEventListener('change', () => {
        item.classList.toggle('selected', cb.checked);
        recalcPrice();
      });
      qtyInput.addEventListener('input', () => recalcPrice());
      item.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).tagName === 'INPUT') return;
        cb.checked = !cb.checked;
        cb.dispatchEvent(new Event('change'));
      });
      container.appendChild(item);
    });
  }

  function buildPackages() {
    const container = document.getElementById('packages-grid')!;
    container.innerHTML = '';
    packages.forEach((p) => {
      const item = el('div', 'package-item', '');
      item.dataset.id = p.id;
      item.innerHTML = `
        <input type="checkbox">
        <div class="package-info">
          <div class="package-name">${esc(p.label)}</div>
          <div class="package-desc">${esc(p.description || '')}</div>
        </div>
        <span class="package-price">$${(p.price_cents / 100).toFixed(2)}</span>
      `;
      const cb = item.querySelector('input[type="checkbox"]') as HTMLInputElement;
      cb.addEventListener('change', () => {
        item.classList.toggle('selected', cb.checked);
        recalcPrice();
      });
      item.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).tagName === 'INPUT') return;
        cb.checked = !cb.checked;
        cb.dispatchEvent(new Event('change'));
      });
      container.appendChild(item);
    });
  }

  // ---- Price calculator (client-side estimate — server re-computes on save) ----
  // Mirrors the widget's `activityCost()` — picks the most-specific
  // pricing_rules row for the selected activity / day / time / party-size and
  // applies the correct rate_type. Server remains authoritative, but the
  // modal preview now matches reality so staff don't see a bogus total.
  function ruleSpecificity(r: PricingRuleRow): number {
    let s = 0;
    if (r.day_of_week != null) s += 2;
    if (r.start_time) s += 2;
    if (r.min_party_size != null || r.max_party_size != null) s += 3;
    return s;
  }

  function computeActivityCost(
    activitySlug: string,
    partySize: number,
    durationHours: number,
    laneCount: number,
    bookingDate: string,
    startHour: number,
  ): { cents: number; label: string } {
    const act = activityBySlug.get(activitySlug);
    if (!act) return { cents: 0, label: '' };

    const rules = pricingRules.filter((r) => r.activity_id === act.id);
    if (rules.length === 0) return { cents: 0, label: '' };

    // Day-of-week from the booking date (local). For the HH:MM used by rule
    // start_time / end_time we use the start hour the staff picked.
    const d = bookingDate ? new Date(`${bookingDate}T00:00:00`) : new Date();
    const dow = d.getDay();
    const hhmm = `${String(startHour).padStart(2, '0')}:00`;

    const eligible = rules.filter((r) => {
      if (r.day_of_week != null && r.day_of_week !== dow) return false;
      if (r.start_time && hhmm < r.start_time) return false;
      if (r.end_time && hhmm >= r.end_time) return false;
      if (r.min_party_size != null && partySize < r.min_party_size) return false;
      if (r.max_party_size != null && partySize > r.max_party_size) return false;
      return true;
    });
    if (eligible.length === 0) return { cents: 0, label: 'no matching rule' };

    // Laser-tag: rate_type per_person_flat, where hour_index ∈ {1,2,3} means
    // rounds and anything above is the day pass. Map duration → rounds.
    if (activitySlug.includes('laser')) {
      const flatRules = eligible.filter((r) => r.rate_type === 'per_person_flat');
      if (flatRules.length > 0) {
        let rounds: number;
        if (durationHours === 1) rounds = 1;
        else if (durationHours === 2) rounds = 2;
        else if (durationHours === 3) rounds = 3;
        else rounds = 999; // "unlimited day pass"
        let match = flatRules.find((r) => r.hour_index === rounds);
        if (!match) {
          // Fall back to the highest-tier flat rule (day pass).
          match = flatRules.slice().sort((a, b) => (b.hour_index ?? 0) - (a.hour_index ?? 0))[0];
        }
        if (match) {
          const cents = match.rate_cents * Math.max(1, partySize);
          return { cents, label: match.label ?? 'laser tag' };
        }
      }
    }

    // Most-specific rule wins (party-size brackets > time-windowed > generic).
    eligible.sort((a, b) => ruleSpecificity(b) - ruleSpecificity(a));
    const rule = eligible[0];
    const units = Math.max(1, laneCount);
    const hours = Math.max(1, durationHours);
    const people = Math.max(1, partySize);

    let cents = 0;
    switch (rule.rate_type) {
      case 'per_person_per_hour':
        cents = rule.rate_cents * people * hours;
        break;
      case 'per_person_flat':
        cents = rule.rate_cents * people;
        break;
      case 'per_hour':
        cents = rule.rate_cents * hours * units;
        break;
      case 'per_round':
        cents = rule.rate_cents * hours;
        break;
      case 'flat':
        cents = rule.rate_cents;
        break;
      default:
        cents = 0;
    }
    return { cents, label: rule.label ?? rule.rate_type };
  }

  function renderMonthOverMonth(
    cur: { revenue_cents: number; bookings: number; no_shows?: number },
    prev: { revenue_cents: number; bookings: number; no_shows?: number },
  ) {
    const fmtMoney = (c: number) => '$' + (c / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });
    const fmtChange = (curV: number, prevV: number): { text: string; cls: string } => {
      if (prevV === 0) return { text: curV > 0 ? 'new' : '—', cls: 'change-neutral' };
      const pct = ((curV - prevV) / prevV) * 100;
      const sign = pct > 0 ? '+' : '';
      const cls = Math.abs(pct) < 0.5 ? 'change-neutral' : pct > 0 ? 'change-positive' : 'change-negative';
      return { text: `${sign}${pct.toFixed(1)}%`, cls };
    };
    const setText = (id: string, text: string, cls?: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.textContent = text;
      if (cls) el.className = cls;
    };

    setText('mom-rev-prev', fmtMoney(prev.revenue_cents));
    setText('mom-rev-cur', fmtMoney(cur.revenue_cents));
    const revChg = fmtChange(cur.revenue_cents, prev.revenue_cents);
    setText('mom-rev-chg', revChg.text, revChg.cls);

    setText('mom-bk-prev', String(prev.bookings));
    setText('mom-bk-cur', String(cur.bookings));
    const bkChg = fmtChange(cur.bookings, prev.bookings);
    setText('mom-bk-chg', bkChg.text, bkChg.cls);

    const avgPrev = prev.bookings > 0 ? Math.round(prev.revenue_cents / prev.bookings) : 0;
    const avgCur = cur.bookings > 0 ? Math.round(cur.revenue_cents / cur.bookings) : 0;
    setText('mom-avg-prev', fmtMoney(avgPrev));
    setText('mom-avg-cur', fmtMoney(avgCur));
    const avgChg = fmtChange(avgCur, avgPrev);
    setText('mom-avg-chg', avgChg.text, avgChg.cls);

    setText('mom-ns-prev', String(prev.no_shows ?? 0));
    setText('mom-ns-cur', String(cur.no_shows ?? 0));
    const nsChg = fmtChange(cur.no_shows ?? 0, prev.no_shows ?? 0);
    // Lower no-shows is better — flip the class.
    setText('mom-ns-chg', nsChg.text, nsChg.cls === 'change-positive' ? 'change-negative' : nsChg.cls === 'change-negative' ? 'change-positive' : nsChg.cls);
  }

  function recalcPrice() {
    const duration = parseInt((document.getElementById('f-duration') as HTMLSelectElement).value) || 1;
    const selectedLanes = document.querySelectorAll('.lane-chip.selected');
    const laneCount = Math.max(selectedLanes.length, 0);
    const activity = (document.getElementById('f-activity') as HTMLSelectElement).value;
    const partySize =
      parseInt((document.getElementById('f-people') as HTMLInputElement).value) || 1;
    const bookingDate = (document.getElementById('f-date') as HTMLInputElement).value;
    // f-time value is minutes-since-midnight; pricing rules key off the hour.
    const startMins =
      parseInt((document.getElementById('f-time') as HTMLSelectElement).value) || 0;
    const startHour = Math.floor(startMins / 60);

    const { cents: laneCost, label: activityLabel } = computeActivityCost(
      activity,
      partySize,
      duration,
      laneCount,
      bookingDate,
      startHour,
    );

    let addonTotal = 0;
    document.querySelectorAll('.addon-item').forEach((item) => {
      const cb = item.querySelector('input[type="checkbox"]') as HTMLInputElement;
      if (!cb.checked) return;
      const id = (item as HTMLElement).dataset.id!;
      const addon = addons.find((a) => a.id === id);
      if (!addon) return;
      const qty = parseInt((item.querySelector('.addon-qty') as HTMLInputElement).value) || 1;
      addonTotal += addon.price_cents * qty;
    });

    let pkgTotal = 0;
    document.querySelectorAll('.package-item').forEach((item) => {
      const cb = item.querySelector('input[type="checkbox"]') as HTMLInputElement;
      if (!cb.checked) return;
      const id = (item as HTMLElement).dataset.id!;
      const pkg = packages.find((p) => p.id === id);
      if (pkg) pkgTotal += pkg.price_cents;
    });

    const totalCents = laneCost + addonTotal + pkgTotal;
    const breakdown: string[] = [];
    if (laneCost > 0) {
      const tag = activityLabel ? `${activityLabel}` : 'Activity';
      breakdown.push(`${tag}: $${(laneCost / 100).toFixed(2)}`);
    }
    if (addonTotal > 0) breakdown.push('Add-ons: $' + (addonTotal / 100).toFixed(2));
    if (pkgTotal > 0) breakdown.push('Packages: $' + (pkgTotal / 100).toFixed(2));

    document.getElementById('price-breakdown')!.textContent = breakdown.join(' + ');
    document.getElementById('price-total-value')!.textContent = '$' + (totalCents / 100).toFixed(2);
  }

  // ---- Open modal (new / edit) ----
  function resetForm() {
    (document.getElementById('booking-form') as HTMLFormElement).reset();
    document.querySelectorAll('.lane-chip.selected').forEach((c) => c.classList.remove('selected'));
    document.querySelectorAll('.addon-item.selected').forEach((c) => {
      c.classList.remove('selected');
      (c.querySelector('input[type="checkbox"]') as HTMLInputElement).checked = false;
      (c.querySelector('.addon-qty') as HTMLInputElement).value = '1';
    });
    document.querySelectorAll('.package-item.selected').forEach((c) => {
      c.classList.remove('selected');
      (c.querySelector('input[type="checkbox"]') as HTMLInputElement).checked = false;
    });
    document.getElementById('price-breakdown')!.textContent = '';
    document.getElementById('price-total-value')!.textContent = '$0.00';
  }

  function showActionButtons(isEdit: boolean, booking?: ApiBooking) {
    document.getElementById('btn-delete-booking')!.classList.toggle('hidden', !isEdit);
    document.getElementById('btn-mark-paid')!.classList.toggle('hidden', !isEdit);
    document.getElementById('btn-print-receipt')!.classList.toggle('hidden', !isEdit);

    // Refund button: only visible for paid bookings with a Stripe PI on file.
    const canRefund = !!(
      isEdit &&
      booking &&
      booking.payment_status === 'paid' &&
      booking.stripe_payment_intent
    );
    const refundBtn = document.getElementById('btn-refund-booking') as HTMLButtonElement | null;
    if (refundBtn) {
      refundBtn.classList.toggle('hidden', !canRefund);
      refundBtn.disabled = false;
      refundBtn.textContent = 'Refund';
    }

    // Rebook button: only on cancelled or refunded bookings.
    const canRebook = !!(
      isEdit &&
      booking &&
      (booking.status === 'cancelled' || booking.payment_status === 'refunded')
    );
    const rebookBtn = document.getElementById('btn-rebook') as HTMLButtonElement | null;
    if (rebookBtn) rebookBtn.classList.toggle('hidden', !canRebook);

    (document.getElementById('btn-save-booking') as HTMLButtonElement).textContent = isEdit
      ? 'Update Booking'
      : 'Save Booking';
  }

  /**
   * Holds the most recently opened booking detail so the Rebook button can
   * pre-fill a fresh booking modal from the same customer/activity/addons.
   * Cleared whenever the modal is closed or a new booking is started.
   */
  let lastOpenedBookingDetail: BookingDetail | null = null;

  function openNewBooking(lane: Lane | null, hour: number) {
    editingBookingId = null;
    resetForm();
    document.getElementById('modal-title')!.textContent = 'Add Booking';
    (document.getElementById('f-date') as HTMLInputElement).value = currentDate;
    // `hour` from grid click is an integer — map to on-the-hour option (minutes).
    (document.getElementById('f-time') as HTMLSelectElement).value = String(hour * 60);
    (document.getElementById('f-activity') as HTMLSelectElement).value = lane
      ? lane.activitySlug
      : '';
    if (lane) {
      buildLanePicker(lane.activitySlug);
      setTimeout(() => {
        const chip = document.querySelector(`.lane-chip[data-id="${lane.id}"]`);
        if (chip) chip.classList.add('selected');
        autoSelectShoeRental(lane.activitySlug);
        recalcPrice();
      }, 10);
    }
    showActionButtons(false);
    openModal('modal-overlay');
    recalcPrice();
  }

  /** Pre-tick the shoe-rental addon for bowling activities. Qty mirrors party
   *  size. Staff can uncheck if the customer brought their own. */
  function autoSelectShoeRental(activitySlug: string) {
    if (activitySlug !== 'bowling-5pin' && activitySlug !== 'bowling-10pin') return;
    const shoeAddon = addons.find((a) => a.slug === 'shoe-rental');
    if (!shoeAddon) return;
    const item = document.querySelector(`.addon-item[data-id="${shoeAddon.id}"]`);
    if (!item) return;
    const cb = item.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
    if (!cb || cb.checked) return;
    cb.checked = true;
    item.classList.add('selected');
    const qtyInput = item.querySelector('.addon-qty') as HTMLInputElement | null;
    if (qtyInput) {
      const partySize = parseInt((document.getElementById('f-people') as HTMLInputElement).value) || 2;
      qtyInput.value = String(Math.max(1, partySize));
    }
  }

  async function openBookingDetail(bookingId: string) {
    let detail: BookingDetail;
    try {
      detail = (await api.getBooking(bookingId)) as unknown as BookingDetail;
    } catch (err) {
      console.error(err);
      alert('Could not load booking.');
      return;
    }
    const b = detail.booking;
    editingBookingId = bookingId;
    resetForm();

    document.getElementById('modal-title')!.textContent = 'Booking Details';
    (document.getElementById('f-name') as HTMLInputElement).value = b.booker_name || '';
    (document.getElementById('f-phone') as HTMLInputElement).value = b.booker_phone || '';
    (document.getElementById('f-email') as HTMLInputElement).value = b.booker_email || '';

    const act = activityById.get(b.activity_id);
    (document.getElementById('f-activity') as HTMLSelectElement).value = act?.slug || '';
    (document.getElementById('f-booking-type') as HTMLSelectElement).value = b.booking_type || 'walkin';

    // Local-tz date / hour
    const start = new Date(b.start_at * 1000);
    const localDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
    (document.getElementById('f-date') as HTMLInputElement).value = localDate;
    // Snap to nearest 30 min (existing bookings may have any minute value).
    const startMinutesOfDay = start.getHours() * 60 + (start.getMinutes() >= 30 ? 30 : 0);
    (document.getElementById('f-time') as HTMLSelectElement).value = String(startMinutesOfDay);
    const durHours = Math.max(1, Math.round((b.end_at - b.start_at) / 3600));
    (document.getElementById('f-duration') as HTMLSelectElement).value = String(durHours);
    (document.getElementById('f-people') as HTMLInputElement).value = String(b.party_size || 2);
    (document.getElementById('f-payment') as HTMLSelectElement).value =
      b.payment_status === 'paid' ? 'paid' : b.payment_status === 'partial' ? 'deposit' : 'unpaid';
    (document.getElementById('f-status') as HTMLSelectElement).value = b.status || 'confirmed';
    (document.getElementById('f-notes') as HTMLTextAreaElement).value = b.notes || '';

    buildLanePicker(act?.slug || '');
    setTimeout(() => {
      detail.resources.forEach((rid) => {
        const chip = document.querySelector(`.lane-chip[data-id="${rid}"]`);
        if (chip) chip.classList.add('selected');
      });
      detail.addons.forEach((a) => {
        const cb = document.querySelector(
          `.addon-item[data-id="${a.addon_id}"] input[type="checkbox"]`,
        ) as HTMLInputElement | null;
        if (cb) {
          cb.checked = true;
          cb.closest('.addon-item')!.classList.add('selected');
          const qtyInput = cb.closest('.addon-item')!.querySelector('.addon-qty') as HTMLInputElement;
          if (qtyInput) qtyInput.value = String(a.qty);
        }
      });
      detail.packages.forEach((p) => {
        const cb = document.querySelector(
          `.package-item[data-id="${p.package_id}"] input[type="checkbox"]`,
        ) as HTMLInputElement | null;
        if (cb) {
          cb.checked = true;
          cb.closest('.package-item')!.classList.add('selected');
        }
      });
      recalcPrice();
      // Override the recalc'd total with the ACTUAL stored total from the
      // booking. recalcPrice uses a fallback rate that can disagree with the
      // server's authoritative pricing rules; we never want to mislead staff
      // about what the customer actually paid.
      if (typeof b.total_cents === 'number') {
        const totalEl = document.getElementById('price-total-value');
        if (totalEl) totalEl.textContent = '$' + (b.total_cents / 100).toFixed(2);
        const breakdownEl = document.getElementById('price-breakdown');
        if (breakdownEl) breakdownEl.textContent = 'Booked total';
      }
    }, 10);

    lastOpenedBookingDetail = detail;
    showActionButtons(true, b);
    openModal('modal-overlay');
  }

  /**
   * Refund the currently-opened booking via Stripe. Confirms with staff,
   * calls the API, then refreshes the modal state from the response so the
   * Refund button disappears and the status reflects the new state.
   */
  async function refundCurrentBooking() {
    if (!editingBookingId || !lastOpenedBookingDetail) return;
    const b = lastOpenedBookingDetail.booking;
    const amountStr = '$' + ((b.total_cents || 0) / 100).toFixed(2);
    const who = b.booker_name || 'this customer';
    if (!confirm(`Refund ${amountStr} to ${who}? This cannot be undone.`)) return;

    const refundBtn = document.getElementById('btn-refund-booking') as HTMLButtonElement | null;
    if (refundBtn) {
      refundBtn.disabled = true;
      refundBtn.textContent = 'Refunding…';
    }
    try {
      const res = await api.refundBooking(editingBookingId);
      // Merge the refreshed booking into local state so the modal reflects it.
      const updated = res.booking as unknown as ApiBooking;
      lastOpenedBookingDetail = { ...lastOpenedBookingDetail, booking: updated };
      (document.getElementById('f-payment') as HTMLSelectElement).value = 'unpaid';
      (document.getElementById('f-status') as HTMLSelectElement).value = 'cancelled';
      if (refundBtn) {
        refundBtn.classList.add('hidden');
        refundBtn.textContent = 'Refunded';
      }
      // Reveal the Rebook button now that state is cancelled/refunded.
      showActionButtons(true, updated);
      void buildGrid();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Refund failed.';
      alert('Stripe error: ' + msg);
      if (refundBtn) {
        refundBtn.disabled = false;
        refundBtn.textContent = 'Refund';
      }
    }
  }

  /**
   * Open a fresh "Add Booking" modal pre-filled from the currently-opened
   * (cancelled/refunded) booking. The original booking row stays intact in
   * history; the new booking is created normally on save.
   *
   * Pre-filled: customer name/email/phone, activity, party size, add-ons,
   * packages, notes (prepended with "Rebooked from #XXXX"). Cleared:
   * resource (lane) selection, date/time — staff picks a fresh slot.
   */
  function rebookCurrentBooking() {
    if (!lastOpenedBookingDetail) return;
    const detail = lastOpenedBookingDetail;
    const b = detail.booking;
    const act = activityById.get(b.activity_id);

    // Reset to a clean "new booking" state, then fill from the source.
    editingBookingId = null;
    resetForm();
    document.getElementById('modal-title')!.textContent = 'Rebook';

    (document.getElementById('f-name') as HTMLInputElement).value = b.booker_name || '';
    (document.getElementById('f-phone') as HTMLInputElement).value = b.booker_phone || '';
    (document.getElementById('f-email') as HTMLInputElement).value = b.booker_email || '';
    (document.getElementById('f-activity') as HTMLSelectElement).value = act?.slug || '';
    (document.getElementById('f-booking-type') as HTMLSelectElement).value = b.booking_type || 'phone';
    (document.getElementById('f-people') as HTMLInputElement).value = String(b.party_size || 2);
    (document.getElementById('f-payment') as HTMLSelectElement).value = 'unpaid';
    (document.getElementById('f-status') as HTMLSelectElement).value = 'confirmed';

    const shortId = b.id.slice(0, 8);
    const noteParts: string[] = [`Rebooked from #${shortId}`];
    if (b.notes) noteParts.push(b.notes);
    (document.getElementById('f-notes') as HTMLTextAreaElement).value = noteParts.join(' — ');

    // Default date to today; staff picks the time.
    (document.getElementById('f-date') as HTMLInputElement).value = currentDate;

    // Rebuild the lane picker for the activity so add-on/package lists render,
    // then re-tick the same add-ons + packages. Lanes are intentionally NOT
    // pre-selected — staff picks a fresh slot.
    buildLanePicker(act?.slug || '');
    setTimeout(() => {
      detail.addons.forEach((a) => {
        const cb = document.querySelector(
          `.addon-item[data-id="${a.addon_id}"] input[type="checkbox"]`,
        ) as HTMLInputElement | null;
        if (cb) {
          cb.checked = true;
          cb.closest('.addon-item')!.classList.add('selected');
          const qtyInput = cb.closest('.addon-item')!.querySelector('.addon-qty') as HTMLInputElement;
          if (qtyInput) qtyInput.value = String(a.qty);
        }
      });
      detail.packages.forEach((p) => {
        const cb = document.querySelector(
          `.package-item[data-id="${p.package_id}"] input[type="checkbox"]`,
        ) as HTMLInputElement | null;
        if (cb) {
          cb.checked = true;
          cb.closest('.package-item')!.classList.add('selected');
        }
      });
      recalcPrice();
    }, 10);

    // Switch the modal into "create" mode so save hits POST, not PATCH.
    showActionButtons(false);
    lastOpenedBookingDetail = null;
  }

  async function saveBooking() {
    const name = (document.getElementById('f-name') as HTMLInputElement).value.trim();
    if (!name) {
      alert('Customer name is required.');
      return;
    }
    const activitySlug = (document.getElementById('f-activity') as HTMLSelectElement).value;
    const date = (document.getElementById('f-date') as HTMLInputElement).value;
    // f-time is minutes-since-midnight (supports :00 and :30 start times).
    const startMins = parseInt((document.getElementById('f-time') as HTMLSelectElement).value);
    const duration = parseInt((document.getElementById('f-duration') as HTMLSelectElement).value);
    if (!activitySlug || !date || isNaN(startMins) || !duration) {
      alert('Please fill in all required fields.');
      return;
    }
    const act = activityBySlug.get(activitySlug);
    if (!act) {
      alert('Unknown activity.');
      return;
    }
    const resourceIds = [...document.querySelectorAll('.lane-chip.selected')].map(
      (c) => (c as HTMLElement).dataset.id!,
    );
    if (resourceIds.length === 0 && (activitySlug.includes('pin') || act.slug.includes('pin'))) {
      alert('Select at least one lane.');
      return;
    }
    const addonsBody: Array<{ addon_id: string; qty: number }> = [];
    document.querySelectorAll('.addon-item').forEach((item) => {
      const cb = item.querySelector('input[type="checkbox"]') as HTMLInputElement;
      if (cb.checked) {
        addonsBody.push({
          addon_id: (item as HTMLElement).dataset.id!,
          qty: parseInt((item.querySelector('.addon-qty') as HTMLInputElement).value) || 1,
        });
      }
    });
    const packagesBody: string[] = [];
    document.querySelectorAll('.package-item').forEach((item) => {
      const cb = item.querySelector('input[type="checkbox"]') as HTMLInputElement;
      if (cb.checked) packagesBody.push((item as HTMLElement).dataset.id!);
    });

    const startEpoch = localMinutesToEpoch(date, startMins);
    const endEpoch = localMinutesToEpoch(date, startMins + duration * 60);

    const paymentMap: Record<string, string> = {
      paid: 'paid',
      deposit: 'partial',
      unpaid: 'pending',
    };
    const paymentUiVal = (document.getElementById('f-payment') as HTMLSelectElement).value;
    const payment = paymentMap[paymentUiVal] || 'pending';

    const body: Record<string, unknown> = {
      activity_id: act.id,
      resource_ids: resourceIds.length > 0 ? resourceIds : [laneList[0]?.id].filter(Boolean),
      start_at: startEpoch,
      end_at: endEpoch,
      booking_type: (document.getElementById('f-booking-type') as HTMLSelectElement).value,
      source: 'admin',
      booker_name: name,
      booker_phone: (document.getElementById('f-phone') as HTMLInputElement).value.trim() || null,
      booker_email: (document.getElementById('f-email') as HTMLInputElement).value.trim() || null,
      party_size: parseInt((document.getElementById('f-people') as HTMLInputElement).value) || 2,
      notes: (document.getElementById('f-notes') as HTMLTextAreaElement).value.trim() || null,
      addons: addonsBody,
      packages: packagesBody,
    };

    const bookingStatus = (document.getElementById('f-status') as HTMLSelectElement).value;

    try {
      if (editingBookingId) {
        // Pre-flight availability check so a lane reassignment can't silently
        // double-book. The PATCH endpoint also enforces this server-side; this
        // call gives staff a clear, fast error before submit.
        if (resourceIds.length > 0) {
          const check = await api.availabilityCheck({
            resource_ids: resourceIds,
            start_at: startEpoch,
            end_at: endEpoch,
            ignore_booking_id: editingBookingId,
          });
          if (!check.available) {
            const lane = laneList.find((l) => l.id === check.conflict?.resource_id);
            const laneLabel = lane?.label ?? 'That lane';
            alert(
              `${laneLabel} is already booked for that time. Pick a different lane.`,
            );
            return;
          }
        }
        await api.updateBooking(editingBookingId, {
          start_at: startEpoch,
          end_at: endEpoch,
          booking_type: body.booking_type,
          booker_name: body.booker_name,
          booker_email: body.booker_email,
          booker_phone: body.booker_phone,
          party_size: body.party_size,
          notes: body.notes,
          resource_ids: resourceIds,
          status: bookingStatus,
          payment_status: payment,
        });
      } else {
        await api.createBooking(body);
      }
      closeModal('modal-overlay');
      void buildGrid();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to save booking.';
      alert(msg);
    }
  }

  async function confirmDelete() {
    if (!editingBookingId) return;
    try {
      await api.deleteBooking(editingBookingId);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to cancel booking.';
      alert(msg);
      return;
    }
    closeModal('confirm-overlay');
    closeModal('modal-overlay');
    editingBookingId = null;
    void buildGrid();
  }

  async function markPaid() {
    // Payment status toggle isn't part of the current PATCH schema; we'd need a
    // dedicated endpoint. For now just flip the UI control and save the rest.
    (document.getElementById('f-payment') as HTMLSelectElement).value = 'paid';
    await saveBooking();
  }

  // ---- Tabs ----
  let reportsLoaded = false;
  let customersLoaded = false;
  let supportLoaded = false;
  let setupLoaded = false;

  function switchTab(
    tabName: 'schedule' | 'reports' | 'support' | 'customers' | 'setup' | 'activity' | 'docs',
  ) {
    document.querySelectorAll('.tab-btn').forEach((btn) => btn.classList.remove('active'));
    document.getElementById('tab-' + tabName)!.classList.add('active');

    const appLayout = document.querySelector('.app-layout') as HTMLElement;
    const sidebar = document.getElementById('summary-sidebar')!;
    const mainContent = document.querySelector('.main-content') as HTMLElement;
    const reportsDash = document.getElementById('reports-dashboard')!;
    const supportDash = document.getElementById('support-dashboard')!;
    const customersDash = document.getElementById('customers-dashboard')!;
    const setupDash = document.getElementById('setup-dashboard')!;
    const activityDash = document.getElementById('panel-activity')!;
    const docsDash = document.getElementById('docs-dashboard')!;
    const newBookingBtn = document.getElementById('btn-new-booking') as HTMLElement;

    const hideAll = () => {
      reportsDash.classList.add('hidden');
      supportDash.classList.add('hidden');
      customersDash.classList.add('hidden');
      setupDash.classList.add('hidden');
      activityDash.classList.add('hidden');
      docsDash.classList.add('hidden');
    };

    // Stop activity polling when switching away
    if (tabName !== 'activity') stopActivityPolling();

    if (tabName === 'schedule') {
      appLayout.classList.remove('layout-hidden');
      sidebar.style.display = '';
      mainContent.style.display = '';
      hideAll();
      newBookingBtn.style.display = '';
    } else if (tabName === 'reports') {
      appLayout.classList.add('layout-hidden');
      sidebar.style.display = 'none';
      mainContent.style.display = 'none';
      hideAll();
      reportsDash.classList.remove('hidden');
      newBookingBtn.style.display = 'none';
      if (!reportsLoaded) {
        reportsLoaded = true;
        void loadReports();
      }
    } else if (tabName === 'support') {
      appLayout.classList.add('layout-hidden');
      sidebar.style.display = 'none';
      mainContent.style.display = 'none';
      hideAll();
      supportDash.classList.remove('hidden');
      newBookingBtn.style.display = 'none';
      if (!supportLoaded) {
        supportLoaded = true;
        void loadSupport();
      }
    } else if (tabName === 'customers') {
      appLayout.classList.add('layout-hidden');
      sidebar.style.display = 'none';
      mainContent.style.display = 'none';
      hideAll();
      customersDash.classList.remove('hidden');
      newBookingBtn.style.display = 'none';
      if (!customersLoaded) {
        customersLoaded = true;
        void loadCustomers();
      }
    } else if (tabName === 'setup') {
      appLayout.classList.add('layout-hidden');
      sidebar.style.display = 'none';
      mainContent.style.display = 'none';
      hideAll();
      setupDash.classList.remove('hidden');
      newBookingBtn.style.display = 'none';
      if (!setupLoaded) {
        setupLoaded = true;
        void loadSetup();
      }
    } else if (tabName === 'activity') {
      appLayout.classList.add('layout-hidden');
      sidebar.style.display = 'none';
      mainContent.style.display = 'none';
      hideAll();
      activityDash.classList.remove('hidden');
      newBookingBtn.style.display = '';
      void loadActivity();
      startActivityPolling();
    } else if (tabName === 'docs') {
      appLayout.classList.add('layout-hidden');
      sidebar.style.display = 'none';
      mainContent.style.display = 'none';
      hideAll();
      docsDash.classList.remove('hidden');
      newBookingBtn.style.display = 'none';
      void mountDocs();
    }
  }

  // ---- Activity tab ----
  type ActivityRange = 'day' | 'week' | 'month';
  type ActivitySortCol = 'time' | 'customer' | 'activity' | 'people' | 'duration' | 'total';
  type ActivitySortDir = 'asc' | 'desc';
  let activityRange: ActivityRange = 'day';
  let activityPollTimer: number | null = null;
  let activitySortCol: ActivitySortCol = 'time';
  let activitySortDir: ActivitySortDir = 'desc';
  let activityRowsCache: Awaited<ReturnType<typeof api.activityFeed>>['bookings'] = [];

  // Month selector state for the Activity tab. 0 = current month,
  // -1 = previous, etc. Only used when activityRange === 'month'.
  const ACTIVITY_MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];
  const activityNow = new Date();
  const activityCurYear = activityNow.getFullYear();
  const activityCurMonth = activityNow.getMonth(); // 0-indexed
  let activityMonthOffset = 0;

  function activitySelectedMonth(): { year: number; month: number } {
    const d = new Date(activityCurYear, activityCurMonth + activityMonthOffset, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  }

  function activityMonthLabel(): string {
    const { year, month } = activitySelectedMonth();
    return `${ACTIVITY_MONTH_NAMES[month]} ${year}`;
  }

  function activityMonthParam(): string {
    const { year, month } = activitySelectedMonth();
    return `${year}-${String(month + 1).padStart(2, '0')}`;
  }

  function rangeLabel(r: ActivityRange): string {
    if (r === 'day') return 'today';
    if (r === 'week') return 'this week';
    return activityMonthLabel();
  }

  function fmtMoneyCents(cents: number): string {
    const dollars = (cents || 0) / 100;
    return `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  function fmtRelative(epochSec: number): string {
    const now = Date.now() / 1000;
    const diff = Math.max(0, now - epochSec);
    if (diff < 60) return 'just now';
    if (diff < 3600) {
      const m = Math.floor(diff / 60);
      return `${m} min ago`;
    }
    if (diff < 86400) {
      const h = Math.floor(diff / 3600);
      return `${h} hour${h === 1 ? '' : 's'} ago`;
    }
    const d = new Date(epochSec * 1000);
    const today = new Date();
    const yesterday = new Date(today.getTime() - 86400_000);
    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (sameDay(d, yesterday)) return `yesterday ${time}`;
    if (diff < 7 * 86400) {
      const days = Math.floor(diff / 86400);
      return `${days} day${days === 1 ? '' : 's'} ago`;
    }
    return d.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  function sourceBadge(bookingType: string, source: string | null): {
    label: string;
    cls: string;
  } {
    const t = (bookingType || '').toLowerCase();
    const s = (source || '').toLowerCase();
    if (t === 'online' || s === 'widget') return { label: 'Online', cls: 'online' };
    if (t === 'walkin') return { label: 'Walk-in', cls: 'walkin' };
    if (t === 'blocked') return { label: 'Blocked', cls: 'blocked' };
    return { label: 'Staff', cls: 'staff' };
  }

  function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (ch) =>
      ch === '&'
        ? '&amp;'
        : ch === '<'
          ? '&lt;'
          : ch === '>'
            ? '&gt;'
            : ch === '"'
              ? '&quot;'
              : '&#39;',
    );
  }

  function durationText(startAt: number, endAt: number): string {
    const mins = Math.max(0, Math.round((endAt - startAt) / 60));
    if (mins <= 0) return '';
    if (mins % 60 === 0) {
      const h = mins / 60;
      return `${h}hr`;
    }
    if (mins < 60) return `${mins}min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}hr ${m}min`;
  }

  function compareActivityRows(
    a: (typeof activityRowsCache)[number],
    b: (typeof activityRowsCache)[number],
    col: ActivitySortCol,
  ): number {
    switch (col) {
      case 'time':
        return (a.created_at || 0) - (b.created_at || 0);
      case 'customer': {
        const an = (a.customer_name || a.booker_name || '').toLowerCase();
        const bn = (b.customer_name || b.booker_name || '').toLowerCase();
        return an.localeCompare(bn);
      }
      case 'activity': {
        const av = (a.activity_label || '').toLowerCase();
        const bv = (b.activity_label || '').toLowerCase();
        return av.localeCompare(bv);
      }
      case 'people':
        return (a.party_size || 0) - (b.party_size || 0);
      case 'duration':
        return (a.end_at - a.start_at) - (b.end_at - b.start_at);
      case 'total':
        return (a.total_cents || 0) - (b.total_cents || 0);
    }
  }

  function renderActivityRows() {
    const tbody = document.getElementById('activity-feed');
    if (!tbody) return;

    // Update header indicators
    const ths = document.querySelectorAll<HTMLTableCellElement>('#activity-table thead th.sortable');
    ths.forEach((th) => {
      th.classList.remove('sort-asc', 'sort-desc');
      if (th.dataset.sort === activitySortCol) {
        th.classList.add(activitySortDir === 'asc' ? 'sort-asc' : 'sort-desc');
      }
    });

    if (!activityRowsCache || activityRowsCache.length === 0) {
      tbody.innerHTML = `<tr><td colspan="10" class="activity-empty">No bookings yet for this period.</td></tr>`;
      return;
    }

    const sorted = [...activityRowsCache].sort((a, b) => {
      const cmp = compareActivityRows(a, b, activitySortCol);
      return activitySortDir === 'asc' ? cmp : -cmp;
    });

    tbody.innerHTML = sorted
      .map((b) => {
        const name = escapeHtml(b.customer_name || b.booker_name || '—');
        const activity = escapeHtml(b.activity_label || 'Booking');
        const unit = escapeHtml(b.unit_label || 'Lane');
        const lanePart =
          b.first_resource_number != null
            ? b.resource_count > 1
              ? `${unit} ${b.first_resource_number} +${b.resource_count - 1} more`
              : `${unit} ${b.first_resource_number}`
            : '—';
        const peopleNum = b.party_size && b.party_size > 0 ? String(b.party_size) : '—';
        const durMins = Math.max(0, Math.round((b.end_at - b.start_at) / 60));
        const durText = durMins > 0
          ? (durMins % 60 === 0 ? `${durMins / 60}hr` : (durMins < 60 ? `${durMins}min` : `${Math.floor(durMins / 60)}hr ${durMins % 60}min`))
          : '—';
        const phone = (b.customer_phone || b.booker_phone || '').trim();
        const email = (b.customer_email || b.booker_email || '').trim();
        const phoneCell = phone
          ? `<a href="tel:${escapeHtml(phone)}" onclick="event.stopPropagation()">${escapeHtml(phone)}</a>`
          : '—';
        const emailCell = email
          ? `<a href="mailto:${escapeHtml(email)}" onclick="event.stopPropagation()">${escapeHtml(email)}</a>`
          : '—';
        const badge = sourceBadge(b.booking_type, b.source);
        const time = fmtRelative(b.created_at);
        const total = fmtMoneyCents(b.total_cents);
        return `<tr data-id="${escapeHtml(b.id)}">
          <td class="act-time">${escapeHtml(time)}</td>
          <td class="act-customer">${name}</td>
          <td class="act-col-phone act-phone">${phoneCell}</td>
          <td class="act-col-email act-email">${emailCell}</td>
          <td>${activity}</td>
          <td class="act-lane">${escapeHtml(lanePart)}</td>
          <td class="act-num">${peopleNum}</td>
          <td class="act-col-duration act-num">${escapeHtml(durText)}</td>
          <td class="act-col-source"><span class="activity-source-badge ${badge.cls}">${badge.label}</span></td>
          <td class="act-num act-total">${total}</td>
        </tr>`;
      })
      .join('');
  }

  async function loadActivity() {
    const tbody = document.getElementById('activity-feed');
    if (!tbody) return;
    try {
      const monthArg =
        activityRange === 'month' ? activityMonthParam() : undefined;
      const data = await api.activityFeed(activityRange, monthArg);
      const label = rangeLabel(activityRange);
      const kpiB = document.getElementById('activity-kpi-bookings');
      const kpiR = document.getElementById('activity-kpi-revenue');
      const kpiA = document.getElementById('activity-kpi-avg');
      const kpiBL = document.getElementById('activity-kpi-bookings-label');
      const kpiRL = document.getElementById('activity-kpi-revenue-label');
      const kpiAL = document.getElementById('activity-kpi-avg-label');
      if (kpiB) kpiB.textContent = String(data.summary.bookings);
      if (kpiR) kpiR.textContent = fmtMoneyCents(data.summary.revenue_cents);
      if (kpiA) kpiA.textContent = fmtMoneyCents(data.summary.avg_booking_cents);
      if (kpiBL) kpiBL.textContent = `Bookings (${label})`;
      if (kpiRL) kpiRL.textContent = `Revenue (${label})`;
      if (kpiAL) kpiAL.textContent = `Avg booking value (${label})`;

      activityRowsCache = data.bookings || [];
      renderActivityRows();
    } catch (err) {
      console.error('[activity] loadActivity failed', err);
      tbody.innerHTML = `<tr><td colspan="10" class="activity-empty">Could not load bookings. Will retry…</td></tr>`;
    }
  }

  function bindActivitySort() {
    const ths = document.querySelectorAll<HTMLTableCellElement>('#activity-table thead th.sortable');
    ths.forEach((th) => {
      th.addEventListener('click', () => {
        const col = th.dataset.sort as ActivitySortCol | undefined;
        if (!col) return;
        if (col === activitySortCol) {
          activitySortDir = activitySortDir === 'asc' ? 'desc' : 'asc';
        } else {
          activitySortCol = col;
          activitySortDir = col === 'time' || col === 'total' || col === 'people' || col === 'duration' ? 'desc' : 'asc';
        }
        renderActivityRows();
      });
    });
  }

  function startActivityPolling() {
    stopActivityPolling();
    activityPollTimer = window.setInterval(() => {
      void loadActivity();
    }, 30000);
  }

  function stopActivityPolling() {
    if (activityPollTimer != null) {
      clearInterval(activityPollTimer);
      activityPollTimer = null;
    }
  }

  function updateActivityMonthNav() {
    const wrap = document.getElementById('activity-month-nav');
    if (wrap) wrap.classList.toggle('hidden', activityRange !== 'month');
    const label = document.getElementById('activity-month-label');
    if (label) label.textContent = activityMonthLabel();
    const nextBtn = document.getElementById(
      'activity-month-next',
    ) as HTMLButtonElement | null;
    if (nextBtn) nextBtn.disabled = activityMonthOffset >= 0;
  }

  function shiftActivityMonth(delta: number) {
    const next = activityMonthOffset + delta;
    if (next > 0) return; // never step past current month
    activityMonthOffset = next;
    updateActivityMonthNav();
    void loadActivity();
  }

  function bindActivityPills() {
    const pills = document.querySelectorAll<HTMLButtonElement>('#activity-range-pills .activity-pill');
    pills.forEach((p) => {
      p.addEventListener('click', () => {
        const r = (p.dataset.range as ActivityRange) || 'day';
        if (r === activityRange) return;
        activityRange = r;
        pills.forEach((q) => {
          const active = q === p;
          q.classList.toggle('active', active);
          q.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        // Reset month offset when switching back to 'month' so we always
        // open on the current calendar month.
        if (r === 'month') activityMonthOffset = 0;
        updateActivityMonthNav();
        void loadActivity();
      });
    });
    document
      .getElementById('activity-month-prev')
      ?.addEventListener('click', () => shiftActivityMonth(-1));
    document
      .getElementById('activity-month-next')
      ?.addEventListener('click', () => shiftActivityMonth(1));
    updateActivityMonthNav();
  }

  // ---- Reports month nav ----
  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const nowForReports = new Date();
  let rptYear = nowForReports.getFullYear();
  let rptMonth = nowForReports.getMonth(); // 0-indexed
  const rptCurYear = nowForReports.getFullYear();
  const rptCurMonth = nowForReports.getMonth();

  function updateReportsMonthLabel() {
    const label = `${MONTH_NAMES[rptMonth]} ${rptYear}`;
    const h2 = document.getElementById('rpt-month-label');
    if (h2) h2.textContent = `${label} — Monthly Report`;
    const addonsTitle = document.getElementById('rpt-addons-title');
    if (addonsTitle) addonsTitle.textContent = `Top Add-Ons Sold — ${label}`;
    const nextBtn = document.getElementById('rpt-month-next') as HTMLButtonElement | null;
    if (nextBtn) {
      const isCurrent = rptYear === rptCurYear && rptMonth === rptCurMonth;
      nextBtn.disabled = isCurrent;
    }
  }

  function shiftReportsMonth(delta: number) {
    const d = new Date(rptYear, rptMonth + delta, 1);
    const newYear = d.getFullYear();
    const newMonth = d.getMonth();
    // Don't go past current month
    if (newYear > rptCurYear || (newYear === rptCurYear && newMonth > rptCurMonth)) return;
    rptYear = newYear;
    rptMonth = newMonth;
    updateReportsMonthLabel();
    // Re-fetch (currently uses same endpoints; data remains as-is until API supports month param)
    void loadReports();
  }

  document.getElementById('rpt-month-prev')?.addEventListener('click', () => shiftReportsMonth(-1));
  document.getElementById('rpt-month-next')?.addEventListener('click', () => shiftReportsMonth(1));
  updateReportsMonthLabel();

  // ---- Reports ----
  async function loadReports() {
    try {
      const monthParam = `${rptYear}-${String(rptMonth + 1).padStart(2, '0')}`;
      const [summary, heatmap, addonsRank, revSource, revCat, byDay, onlineStaff] =
        await Promise.all([
          api.reportsSummary('month', monthParam),
          api.reportsHeatmap(30, monthParam),
          api.reportsAddonsRanking(30, monthParam),
          api.reportsRevenueBySource(30, monthParam),
          api.reportsRevenueByCategory(30, monthParam),
          api.reportsBookingsByDay(90, monthParam),
          api.reportsOnlineVsStaffWeekly(4, monthParam),
        ]);
      renderReports(summary, heatmap, addonsRank, revSource, revCat, byDay, onlineStaff);
    } catch (err) {
      console.error('[admin] reports load failed', err);
    }
  }

  function renderReports(
    summary: Awaited<ReturnType<typeof api.reportsSummary>>,
    heatmap: Awaited<ReturnType<typeof api.reportsHeatmap>>,
    addonsRank: Awaited<ReturnType<typeof api.reportsAddonsRanking>>,
    revSource: Awaited<ReturnType<typeof api.reportsRevenueBySource>>,
    revCat: Awaited<ReturnType<typeof api.reportsRevenueByCategory>>,
    byDay: Awaited<ReturnType<typeof api.reportsBookingsByDay>>,
    onlineStaff: Awaited<ReturnType<typeof api.reportsOnlineVsStaffWeekly>>,
  ) {
    const monthTotals =
      (summary.totals.month as {
        revenue_cents: number;
        bookings: number;
        party: number;
        no_shows?: number;
        cancelled?: number;
      }) || { revenue_cents: 0, bookings: 0, party: 0, no_shows: 0, cancelled: 0 };
    const lastMonthTotals =
      (summary.totals.last_month as typeof monthTotals | undefined) ||
      { revenue_cents: 0, bookings: 0, party: 0, no_shows: 0, cancelled: 0 };
    renderMonthOverMonth(monthTotals, lastMonthTotals);
    // Replace the top report summary cards with real numbers while preserving structure.
    const cards = document.querySelectorAll('#reports-dashboard .report-summary-cards .rpt-card');
    if (cards.length >= 5) {
      const avgCents = monthTotals.bookings
        ? Math.round(monthTotals.revenue_cents / monthTotals.bookings)
        : 0;
      const totalOnline = revSource.sources.find((s) => s.source === 'widget')?.bookings ?? 0;
      const totalStaff = monthTotals.bookings - totalOnline;
      const onlinePct = monthTotals.bookings
        ? Math.round((totalOnline / monthTotals.bookings) * 100)
        : 0;
      const staffPct = 100 - onlinePct;
      setCardValue(cards[0], '$' + (monthTotals.revenue_cents / 100).toLocaleString());
      setCardValue(cards[1], String(monthTotals.bookings));
      setCardValue(cards[2], '$' + (avgCents / 100).toFixed(2));
      setCardValue(cards[3], `${onlinePct}% / ${staffPct}%`);
      setCardValue(cards[4], String(monthTotals.no_shows ?? 0));
    }

    renderDonut(revSource);
    renderRevenueByCategory(revCat);
    renderBookingsByDay(byDay);
    renderOnlineVsStaffWeekly(onlineStaff);
    renderHeatmap(heatmap);
    renderAddonsTable(addonsRank);
  }

  function setCardValue(card: Element, value: string) {
    const el = card.querySelector('.rpt-card-value');
    // We set innerHTML rather than textContent so values that include formatted
    // spans (like "89 (36%)") render correctly. Values come from trusted API.
    if (el) el.innerHTML = value;
  }

  function renderDonut(data: Awaited<ReturnType<typeof api.reportsRevenueBySource>>) {
    const wrapper = document.querySelector('#reports-dashboard .donut-wrapper');
    if (!wrapper) return;
    const total = data.total_cents || 1;
    const legend = wrapper.querySelector('.donut-legend');
    const totalEl = wrapper.querySelector('.donut-total');
    if (totalEl) totalEl.textContent = '$' + (data.total_cents / 100).toLocaleString();
    const colorMap: Record<string, string> = {
      widget: '#3b82f6',
      phone: '#f59e0b',
      admin: '#22c55e',
      walkin: '#22c55e',
    };
    const labelMap: Record<string, string> = {
      widget: 'Online Bookings',
      phone: 'Phone Bookings',
      admin: 'Walk-in Bookings',
      walkin: 'Walk-in Bookings',
    };
    if (legend) {
      legend.innerHTML = data.sources
        .map((s) => {
          const pct = Math.round((s.revenue_cents / total) * 100);
          return `<div class="donut-legend-item">
            <span class="donut-dot" style="background:${colorMap[s.source] || '#64748b'}"></span>
            <span class="donut-legend-label">${labelMap[s.source] || s.source}</span>
            <span class="donut-legend-value">${pct}% — $${(s.revenue_cents / 100).toLocaleString()}</span>
          </div>`;
        })
        .join('');
    }
    const svg = wrapper.querySelector('.donut-svg');
    if (svg) {
      let offset = 25;
      svg.innerHTML = data.sources
        .map((s) => {
          const pct = (s.revenue_cents / total) * 100;
          const color = colorMap[s.source] || '#64748b';
          const circle = `<circle cx="18" cy="18" r="15.915" fill="none" stroke="${color}" stroke-width="3.5" stroke-dasharray="${pct.toFixed(1)} ${(100 - pct).toFixed(1)}" stroke-dashoffset="${offset}"></circle>`;
          offset -= pct;
          return circle;
        })
        .join('');
    }
  }

  function renderRevenueByCategory(data: Awaited<ReturnType<typeof api.reportsRevenueByCategory>>) {
    const chart = document.querySelector('#reports-dashboard .hbar-chart');
    if (!chart) return;
    const max = data.categories.reduce((m, c) => Math.max(m, c.revenue_cents), 1);
    const colors = [
      '#6366f1',
      '#0891b2',
      '#f59e0b',
      '#ef4444',
      '#a855f7',
      '#ec4899',
      '#14b8a6',
      '#f97316',
      '#64748b',
      '#94a3b8',
    ];
    chart.innerHTML = data.categories
      .map(
        (c, i) => `
      <div class="hbar-row">
        <span class="hbar-label">${esc(c.label)}</span>
        <div class="hbar-track"><div class="hbar-fill" style="width:${((c.revenue_cents / max) * 100).toFixed(0)}%;background:${colors[i % colors.length]}"></div></div>
        <span class="hbar-value">$${(c.revenue_cents / 100).toLocaleString()}</span>
      </div>`,
      )
      .join('');
  }

  function renderBookingsByDay(data: Awaited<ReturnType<typeof api.reportsBookingsByDay>>) {
    const chart = document.querySelector('#reports-dashboard .vbar-chart');
    if (!chart) return;
    const labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    // Legacy shows Mon-Sun order
    const ordered = [1, 2, 3, 4, 5, 6, 0].map((dow) => ({
      dow,
      label: labels[dow],
      value: data.days_of_week.find((d) => d.dow === dow)?.avg_bookings || 0,
    }));
    const max = Math.max(1, ...ordered.map((o) => o.value));
    chart.innerHTML = ordered
      .map(
        (o) => `
      <div class="vbar-col">
        <div class="vbar-bar-wrapper"><div class="vbar-bar${o.dow === 5 || o.dow === 6 ? ' accent' : ''}" style="height:${((o.value / max) * 100).toFixed(0)}%" data-val="${o.value}"></div></div>
        <span class="vbar-label">${o.label}</span>
      </div>`,
      )
      .join('');
  }

  function renderOnlineVsStaffWeekly(
    data: Awaited<ReturnType<typeof api.reportsOnlineVsStaffWeekly>>,
  ) {
    const chart = document.querySelector('#reports-dashboard .stacked-bar-chart');
    if (!chart) return;
    chart.innerHTML = data.weeks
      .map((w) => {
        const total = Math.max(1, w.online + w.staff);
        const onlinePct = ((w.online / total) * 100).toFixed(1);
        const staffPct = ((w.staff / total) * 100).toFixed(1);
        return `
        <div class="stacked-row">
          <span class="stacked-label">${esc(w.label)}</span>
          <div class="stacked-track">
            <div class="stacked-fill online" style="width:${onlinePct}%" title="Online: ${w.online}">${w.online}</div>
            <div class="stacked-fill staff" style="width:${staffPct}%" title="Staff: ${w.staff}">${w.staff}</div>
          </div>
        </div>`;
      })
      .join('');
  }

  function renderHeatmap(data: Awaited<ReturnType<typeof api.reportsHeatmap>>) {
    const grid = document.getElementById('heatmap-grid')!;
    grid.innerHTML = '';
    const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const dayDowOrder = [1, 2, 3, 4, 5, 6, 0];
    const hours: number[] = [];
    for (let h = 10; h <= 24; h++) hours.push(h);

    // Normalise: find max count → scale 0..10
    const max = Math.max(1, ...data.cells.map((c) => c.bookings));
    const cellMap = new Map<string, number>();
    data.cells.forEach((c) => cellMap.set(`${c.dow}_${c.hour}`, Math.round((c.bookings / max) * 10)));

    const corner = el('div', 'heatmap-header', '');
    grid.appendChild(corner);
    hours.forEach((h) => {
      const label = formatHour(h === 24 ? 0 : h);
      grid.appendChild(el('div', 'heatmap-header', label));
    });

    dayLabels.forEach((day, idx) => {
      const dow = dayDowOrder[idx];
      grid.appendChild(el('div', 'heatmap-day-label', day));
      hours.forEach((h) => {
        const actualHour = h === 24 ? 0 : h;
        const val = cellMap.get(`${dow}_${actualHour}`) || 0;
        const cell = el('div', 'heatmap-cell', val > 0 ? String(val) : '');
        const intensity = val / 10;
        if (val === 0) {
          cell.style.background = '#f8fafc';
          cell.style.color = 'transparent';
        } else {
          const r = Math.round(240 - intensity * 206);
          const g = Math.round(253 - intensity * 108);
          const b = Math.round(244 - intensity * 183);
          cell.style.background = `rgb(${r},${g},${b})`;
          cell.style.color = intensity > 0.6 ? '#fff' : 'rgba(0,0,0,0.5)';
        }
        grid.appendChild(cell);
      });
    });
  }

  function renderAddonsTable(data: Awaited<ReturnType<typeof api.reportsAddonsRanking>>) {
    const tbody = document.querySelector('#reports-dashboard .rpt-table tbody');
    if (!tbody) return;
    tbody.innerHTML = data.rankings
      .map(
        (r, i) =>
          `<tr><td>${i + 1}</td><td>${esc(r.label)}</td><td>${r.total_qty}</td><td>$${(r.revenue_cents / 100).toLocaleString()}</td></tr>`,
      )
      .join('');
  }

  // ---- Support tab ----
  async function loadSupport() {
    // Legacy support tab renders the static ticket history + form. We leave
    // the DOM content alone so the visual design stays byte-identical. The
    // form submit wiring below still binds, and the placeholder "request
    // submitted" alert fires as before.
    const form = document.getElementById('support-form') as HTMLFormElement | null;
    if (!form) return;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const subject = (document.getElementById('support-subject') as HTMLInputElement).value.trim();
      if (!subject) return;
      alert('Request submitted! Our team will review it within 24 hours.');
      form.reset();
    });
  }

  // ---- Customers tab ----
  let custSortCol = 'lastVisit';
  let custSortDir: 'asc' | 'desc' = 'desc';
  let custData: EnrichedCustomer[] = [];
  let custFilteredData: EnrichedCustomer[] = [];

  function formatLastVisit(epoch: number | null): string {
    if (!epoch) return '—';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const d = new Date(epoch * 1000);
    return months[d.getMonth()] + ' ' + d.getDate();
  }
  function formatJoinDate(epoch: number | null): string {
    if (!epoch) return '—';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const d = new Date(epoch * 1000);
    return months[d.getMonth()] + ' ' + d.getFullYear();
  }

  function renderCustomerTable(data: EnrichedCustomer[]) {
    const tbody = document.getElementById('cust-tbody')!;
    if (!data.length) {
      tbody.innerHTML =
        '<tr><td colspan="11" style="text-align:center;padding:2rem;color:var(--text-muted)">No customers match your search.</td></tr>';
      return;
    }
    tbody.innerHTML = data
      .map((c) => {
        const isVip = c.tags?.includes?.('vip') || c.tags?.includes?.('VIP');
        const isFlagged = c.tags?.includes?.('followup');
        const memberBadge =
          c.is_member === 1
            ? '<span class="cust-member-badge">&#10003; Member</span>'
            : '<span style="color:var(--text-muted)">—</span>';
        const emailCell = c.email
          ? `<span class="cust-email">${esc(c.email)}</span>`
          : `<span class="cust-no-data">—</span>`;
        const phoneCell = c.phone
          ? `<span class="cust-phone">${esc(c.phone)}</span>`
          : `<span class="cust-no-data">—</span>`;
        const vipBadge = isVip
          ? ' <span style="color:#f59e0b;font-size:0.75rem">★ VIP</span>'
          : '';
        const flagBadge = isFlagged
          ? ' <span style="color:var(--danger);font-size:0.75rem">⚑</span>'
          : '';
        const freqClass =
          'freq-badge freq-' +
          (c.frequency || 'Occasional').toLowerCase().replace(/[^a-z]/g, '-');
        const spentDollars = Math.round(c.lifetime_value_cents / 100);
        const avgDollars = Math.round(c.avg_spend_cents / 100);
        return `<tr data-cust-id="${c.id}">
        <td><span class="cust-name">${esc(c.name)}${vipBadge}${flagBadge}</span></td>
        <td>${emailCell}</td>
        <td>${phoneCell}</td>
        <td>${c.total_visits}</td>
        <td><span class="cust-spent">$${spentDollars.toLocaleString()}</span></td>
        <td><span class="cust-date">${formatLastVisit(c.last_visit_at)}</span></td>
        <td><span class="cust-date">${formatJoinDate(c.joined_at)}</span></td>
        <td><span class="cust-spent">$${avgDollars || '—'}</span></td>
        <td><span class="${freqClass}">${esc(c.frequency || '—')}</span></td>
        <td>${memberBadge}</td>
        <td><button class="cust-action-btn" data-cust-id="${c.id}">View</button></td>
      </tr>`;
      })
      .join('');
    tbody.querySelectorAll('tr').forEach((row) => {
      row.addEventListener('click', () => {
        const id = (row as HTMLElement).dataset.custId;
        if (id) void openCustModal(id);
      });
    });
  }

  function applyCustomerFilter() {
    const search = (document.getElementById('cust-search') as HTMLInputElement).value
      .toLowerCase()
      .trim();
    const filter = (document.getElementById('cust-filter') as HTMLSelectElement).value;
    const tagFilterEl = document.getElementById('cust-tag-filter') as HTMLSelectElement | null;
    const tagFilter = tagFilterEl ? tagFilterEl.value.toLowerCase() : '';

    let data = custData.filter((c) => {
      if (search) {
        const hay = `${c.name} ${c.email || ''} ${c.phone || ''}`.toLowerCase();
        if (!hay.includes(search)) return false;
      }
      if (tagFilter) {
        const has = (c.tags || []).some((t) => t.toLowerCase() === tagFilter);
        if (!has) return false;
      }
      if (filter === 'members') return c.is_member === 1;
      if (filter === 'repeat') return c.total_visits >= 2;
      if (filter === 'new') return c.new_this_month === 1;
      return true;
    });

    data.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      switch (custSortCol) {
        case 'name':
          av = a.name.toLowerCase();
          bv = b.name.toLowerCase();
          break;
        case 'visits':
          av = a.total_visits;
          bv = b.total_visits;
          break;
        case 'spent':
          av = a.lifetime_value_cents;
          bv = b.lifetime_value_cents;
          break;
        case 'lastVisit':
          av = a.last_visit_at || 0;
          bv = b.last_visit_at || 0;
          break;
        case 'joined':
          av = a.joined_at || 0;
          bv = b.joined_at || 0;
          break;
        case 'avgSpend':
          av = a.avg_spend_cents;
          bv = b.avg_spend_cents;
          break;
        default:
          av = a.name.toLowerCase();
          bv = b.name.toLowerCase();
      }
      if (typeof av === 'string' && typeof bv === 'string') {
        return custSortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return custSortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });

    custFilteredData = data;
    renderCustomerTable(data);

    document.querySelectorAll('.cust-table th.sortable').forEach((th) => {
      th.classList.remove('sort-asc', 'sort-desc');
      if ((th as HTMLElement).dataset.col === custSortCol) {
        th.classList.add(custSortDir === 'asc' ? 'sort-asc' : 'sort-desc');
      }
    });
  }

  async function openCustModal(id: string) {
    let payload: { customer: Record<string, unknown>; bookings: Array<Record<string, unknown>> };
    try {
      payload = await api.customerHistory(id);
    } catch (err) {
      console.error(err);
      alert('Could not load customer.');
      return;
    }
    const c = custData.find((x) => x.id === id);
    if (!c) return;

    document.getElementById('cust-modal-name')!.textContent = c.name;
    const memberLabel =
      c.is_member === 1 ? '<span class="cust-member-badge">&#10003; Member</span>' : '—';
    const freqClass =
      'freq-badge freq-' + (c.frequency || 'Occasional').toLowerCase().replace(/[^a-z]/g, '-');
    const tagsHtml = (c.tags || [])
      .map((t) => `<span class="cust-tag-pill">${esc(t)}</span>`)
      .join('');
    document.getElementById('cust-detail-meta')!.innerHTML = `
      <div class="cust-since-banner">Customer Since: <strong>${formatJoinDate(c.joined_at)}</strong></div>
      <div class="cust-detail-stats-row">
        <div class="cust-detail-stat-card cust-ltv-card">
          <span class="cust-detail-stat-value">$${Math.round(c.lifetime_value_cents / 100).toLocaleString()}</span>
          <span class="cust-detail-stat-label">Total Lifetime Value</span>
        </div>
        <div class="cust-detail-stat-card">
          <span class="cust-detail-stat-value">$${Math.round(c.avg_spend_cents / 100)}</span>
          <span class="cust-detail-stat-label">Avg Spend Per Visit</span>
        </div>
        <div class="cust-detail-stat-card">
          <span class="cust-detail-stat-value">${c.total_visits}</span>
          <span class="cust-detail-stat-label">Total Visits</span>
        </div>
        <div class="cust-detail-stat-card">
          <span class="cust-detail-stat-value"><span class="${freqClass}">${esc(c.frequency || '—')}</span></span>
          <span class="cust-detail-stat-label">Visit Frequency</span>
        </div>
      </div>
      <div class="cust-detail-contact-row">
        <div class="cust-detail-meta-item">
          <span class="cust-detail-meta-label">Email</span>
          <span class="cust-detail-meta-value">${esc(c.email || '—')}</span>
        </div>
        <div class="cust-detail-meta-item">
          <span class="cust-detail-meta-label">Phone</span>
          <span class="cust-detail-meta-value">${esc(c.phone || '—')}</span>
        </div>
        <div class="cust-detail-meta-item">
          <span class="cust-detail-meta-label">Last Visit</span>
          <span class="cust-detail-meta-value">${formatLastVisit(c.last_visit_at)}</span>
        </div>
        <div class="cust-detail-meta-item">
          <span class="cust-detail-meta-label">Last Activity</span>
          <span class="cust-detail-meta-value">${esc(c.last_activity || '—')}</span>
        </div>
        <div class="cust-detail-meta-item">
          <span class="cust-detail-meta-label">Member</span>
          <span class="cust-detail-meta-value">${memberLabel}</span>
        </div>
      </div>
      ${tagsHtml ? `<div class="cust-tags-row">${tagsHtml}</div>` : ''}
    `;

    // Booking history from API
    const historyBody = document.getElementById('cust-history-tbody')!;
    historyBody.innerHTML = (payload.bookings as Array<Record<string, unknown>>)
      .map((b) => {
        const start = new Date((b.start_at as number) * 1000);
        const months = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        const dateStr = `${months[start.getMonth()]} ${start.getDate()}, ${start.getFullYear()}`;
        const act = activityById.get(b.activity_id as string);
        const amount = '$' + ((b.total_cents as number) / 100).toFixed(2);
        return `<tr><td>${dateStr}</td><td>${esc(act?.label || '—')}</td><td>—</td><td style="font-weight:600;color:var(--success)">${amount}</td></tr>`;
      })
      .join('');

    // Notes
    (document.getElementById('cust-notes-field') as HTMLTextAreaElement).value =
      c.staff_notes || '';

    const vipBtn = document.getElementById('cust-tag-vip')!;
    const flagBtn = document.getElementById('cust-tag-followup')!;
    const isVip = (c.tags || []).some((t) => t.toLowerCase() === 'vip');
    const isFlagged = (c.tags || []).some((t) => t.toLowerCase() === 'followup');
    vipBtn.classList.toggle('active', isVip);
    flagBtn.classList.toggle('active', isFlagged);

    (document.getElementById('cust-modal-save') as HTMLButtonElement).onclick = async () => {
      const notes = (document.getElementById('cust-notes-field') as HTMLTextAreaElement).value;
      const nextTags = [...(c.tags || [])];
      // Simplified: we only update staff_notes here. Tag toggles happen in their own click handler.
      try {
        await api.updateCustomer(id, { staff_notes: notes, tags: nextTags });
        c.staff_notes = notes;
        c.tags = nextTags;
        applyCustomerFilter();
        closeModal('cust-modal-overlay');
      } catch (err) {
        console.error(err);
        alert('Could not save notes.');
      }
    };

    (vipBtn as HTMLButtonElement).onclick = async () => {
      const tagSet = new Set((c.tags || []).map((t) => t.toLowerCase()));
      if (tagSet.has('vip')) tagSet.delete('vip');
      else tagSet.add('vip');
      c.tags = [...tagSet];
      vipBtn.classList.toggle('active', tagSet.has('vip'));
      try {
        await api.updateCustomer(id, { tags: c.tags });
      } catch (err) {
        console.error(err);
      }
    };
    (flagBtn as HTMLButtonElement).onclick = async () => {
      const tagSet = new Set((c.tags || []).map((t) => t.toLowerCase()));
      if (tagSet.has('followup')) tagSet.delete('followup');
      else tagSet.add('followup');
      c.tags = [...tagSet];
      flagBtn.classList.toggle('active', tagSet.has('followup'));
      try {
        await api.updateCustomer(id, { tags: c.tags });
      } catch (err) {
        console.error(err);
      }
    };

    openModal('cust-modal-overlay');
  }

  /** Build query string mirroring the current Activity tab filters. The CSV
   *  endpoints accept from/to/activity_id — we translate the range pill +
   *  selected month into a from/to window so the export matches what the
   *  operator is looking at. */
  function buildBookingsExportQuery(): string {
    const params = new URLSearchParams();
    const now = Math.floor(Date.now() / 1000);
    let fromSec: number | null = null;
    let toSec: number | null = null;
    if (activityRange === 'day') {
      fromSec = now - 24 * 60 * 60;
      toSec = now;
    } else if (activityRange === 'week') {
      fromSec = now - 7 * 24 * 60 * 60;
      toSec = now;
    } else if (activityRange === 'month') {
      const { year, month } = activitySelectedMonth();
      fromSec = Math.floor(Date.UTC(year, month, 1) / 1000);
      toSec = Math.floor(Date.UTC(year, month + 1, 1) / 1000) - 1;
    }
    if (fromSec != null) params.set('from', new Date(fromSec * 1000).toISOString());
    if (toSec != null) params.set('to', new Date(toSec * 1000).toISOString());
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }

  /** Trigger a CSV download from a tenant-scoped endpoint. We fetch with
   *  credentials so the session cookie travels, then turn the body into a
   *  Blob URL and click an anchor — matches the customer export UX. */
  async function downloadCsv(path: string, fallbackFilename: string): Promise<void> {
    const apiOrigin = (import.meta.env.VITE_API_ORIGIN as string | undefined) || '';
    const url = (apiOrigin ? apiOrigin.replace(/\/$/, '') : '') + path;
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        console.error('[csv export] failed', res.status);
        alert(`Export failed (HTTP ${res.status}).`);
        return;
      }
      // Pull the filename out of Content-Disposition when present.
      const cd = res.headers.get('Content-Disposition') || '';
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m?.[1] || fallbackFilename;
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('[csv export] error', err);
      alert('Export failed. Check the console.');
    }
  }

  function exportBookingsCSV(): void {
    const qs = buildBookingsExportQuery();
    void downloadCsv(`/api/t/${encodeURIComponent(slug)}/bookings/export.csv${qs}`, `${slug}-bookings.csv`);
  }

  function exportPaymentsCSV(): void {
    const qs = buildBookingsExportQuery();
    void downloadCsv(`/api/t/${encodeURIComponent(slug)}/payments/export.csv${qs}`, `${slug}-payments.csv`);
  }

  function exportCustomerCSV() {
    const headers = [
      'Name',
      'Email',
      'Phone',
      'Total Visits',
      'Total Spent',
      'Last Visit',
      'Joined',
      'Avg Spend',
      'Frequency',
      'Member',
      'Tags',
    ];
    const rows = custFilteredData.map((c) => [
      c.name,
      c.email || '',
      c.phone || '',
      c.total_visits,
      '$' + (c.lifetime_value_cents / 100).toFixed(2),
      c.last_visit_at ? new Date(c.last_visit_at * 1000).toISOString().slice(0, 10) : '',
      c.joined_at ? new Date(c.joined_at * 1000).toISOString().slice(0, 10) : '',
      c.avg_spend_cents ? '$' + Math.round(c.avg_spend_cents / 100) : '',
      c.frequency || '',
      c.is_member === 1 ? 'Yes' : 'No',
      (c.tags || []).join(', '),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}-customers.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function populateTagFilter() {
    const sel = document.getElementById('cust-tag-filter') as HTMLSelectElement | null;
    if (!sel) return;
    const tagSet = new Set<string>();
    custData.forEach((c) => (c.tags || []).forEach((t) => { if (t) tagSet.add(t); }));
    const tags = [...tagSet].sort((a, b) => a.localeCompare(b));
    const current = sel.value;
    sel.innerHTML =
      '<option value="">All Tags</option>' +
      tags.map((t) => `<option value="${t.replace(/"/g, '&quot;')}">${t}</option>`).join('');
    // preserve selection if still valid
    if (current && tags.some((t) => t.toLowerCase() === current.toLowerCase())) {
      sel.value = current;
    }
  }

  async function loadCustomers() {
    try {
      const [statsResp, listResp] = await Promise.all([
        api.customersStats(),
        api.customersEnriched(),
      ]);
      // Update summary cards (top)
      const cards = document.querySelectorAll(
        '#customers-dashboard .report-summary-cards .rpt-card',
      );
      if (cards.length >= 7) {
        setCardValue(cards[0], String(statsResp.total));
        setCardValue(cards[1], String(statsResp.new_this_month));
        const repeatPct = statsResp.total
          ? Math.round((statsResp.repeat_2plus / statsResp.total) * 100)
          : 0;
        setCardValue(
          cards[2],
          `${statsResp.repeat_2plus} <span style="font-size:1rem;font-weight:600;color:var(--text-muted)">(${repeatPct}%)</span>`,
        );
        const emailsPct = statsResp.total
          ? Math.round((statsResp.emails_collected / statsResp.total) * 100)
          : 0;
        setCardValue(
          cards[3],
          `${statsResp.emails_collected} <span style="font-size:1rem;font-weight:600;color:var(--text-muted)">(${emailsPct}%)</span>`,
        );
        setCardValue(cards[4], String(statsResp.members));
        setCardValue(cards[5], '$' + Math.round(statsResp.avg_value_cents / 100));
        setCardValue(cards[6], statsResp.avg_visits.toFixed(1));
      }
      // Cards' inner HTML uses innerHTML to preserve the "(XX%)" span.
      custData = listResp.customers as unknown as EnrichedCustomer[];
      populateTagFilter();
      applyCustomerFilter();
    } catch (err) {
      console.error('[admin] customers load failed', err);
    }
  }

  // ===========================================================================
  // Setup tab — Lanes / Resources, Addons, Packages, Pricing, Change Log.
  // ===========================================================================

  // In-memory snapshots the tables render from. Loaded on tab open and
  // refreshed after every mutation so the audit trail includes the most
  // recent change immediately.
  let setupResources: ApiResource[] = [];
  let setupAddons: ApiAddon[] = [];
  let setupPackages: ApiPackage[] = [];
  interface PricingRule {
    id: string;
    activity_id: string;
    day_of_week: number | null;
    start_time: string | null;
    end_time: string | null;
    rate_type: string;
    rate_cents: number;
    hour_index: number | null;
    label: string | null;
    min_party_size: number | null;
    max_party_size: number | null;
    valid_from: number | null;
    valid_to: number | null;
  }
  let setupPricing: PricingRule[] = [];
  interface EventRec {
    id: string;
    name: string;
    recurrence: string | null; // JSON {days:[...], start, end}
    theme_color: string | null;
    activity_id: string | null;
    enabled: 0 | 1;
  }
  interface EventRecurrence {
    days: number[];
    start: string;
    end: string;
  }
  let setupEvents: EventRec[] = [];
  function parseRecurrence(r: string | null): EventRecurrence | null {
    if (!r) return null;
    try {
      const v = JSON.parse(r) as EventRecurrence;
      if (!v || !Array.isArray(v.days)) return null;
      return v;
    } catch {
      return null;
    }
  }

  function wireSetupSubtabs() {
    const tabs = document.querySelectorAll<HTMLButtonElement>('.setup-subtab');
    if (!tabs.length) return;
    if ((tabs[0] as HTMLElement).dataset.wired === '1') return;
    const STORAGE_KEY = 'lb-admin-setup-subtab';
    const sections = document.querySelectorAll<HTMLElement>('.setup-section');
    const activate = (sub: string) => {
      tabs.forEach((t) => t.classList.toggle('active', t.dataset.sub === sub));
      sections.forEach((s) => s.classList.toggle('active', s.id === 'setup-section-' + sub));
      try { localStorage.setItem(STORAGE_KEY, sub); } catch {}
    };
    tabs.forEach((t) => {
      t.dataset.wired = '1';
      t.addEventListener('click', () => {
        const sub = t.dataset.sub;
        if (sub) activate(sub);
      });
    });
    // Restore persisted tab
    let saved: string | null = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch {}
    const valid = ['venue', 'resources', 'addons', 'packages', 'pricing', 'branding', 'audit'];
    activate(saved && valid.includes(saved) ? saved : 'venue');
  }

  async function loadSetup() {
    wireSetupSubtabs();
    await reloadSetupData();
    await loadAuditLog();

    // Wire table-header buttons once
    document.getElementById('btn-add-resource')!.addEventListener('click', () => openResourceModal(null));
    document.getElementById('btn-add-addon')!.addEventListener('click', () => openAddonModal(null));
    document.getElementById('btn-add-package')!.addEventListener('click', () => openPackageModal(null));
    document.getElementById('btn-add-pricing')!.addEventListener('click', () => openPricingModal(null));
    document.getElementById('btn-add-event')!.addEventListener('click', () => openEventModal(null));
    document.getElementById('btn-audit-refresh')!.addEventListener('click', () => void loadAuditLog());
    document.getElementById('audit-entity-filter')!.addEventListener('change', () => void loadAuditLog());
    document.getElementById('audit-from')!.addEventListener('change', () => void loadAuditLog());
    document.getElementById('audit-to')!.addEventListener('change', () => void loadAuditLog());

    // Setup modal wiring (shared across all four entity types)
    document.getElementById('setup-modal-close')!.addEventListener('click', () =>
      closeModal('setup-modal-overlay'),
    );
    document.getElementById('setup-modal-cancel')!.addEventListener('click', () =>
      closeModal('setup-modal-overlay'),
    );
    document.getElementById('setup-modal-overlay')!.addEventListener('click', (e) => {
      if (e.target === document.getElementById('setup-modal-overlay')) closeModal('setup-modal-overlay');
    });

    // Stripe panel — gated to platform admins (Mario). Everyone else doesn't
    // even see the section heading. GET /settings/stripe is requirePlan('scale')
    // server-side, so we must also check the plan client-side: firing it on a
    // non-Scale tenant returns 402 plan_required, which the global fetch
    // interceptor turns into a blocking upgrade modal the user never asked for
    // (it covers the rest of the Setup page). Same self-hide pattern as
    // wireConnectPanel().
    if (isPlatformAdmin && (tenant as { plan?: string } | undefined)?.plan === 'scale') {
      const stripePanel = document.getElementById('stripe-panel');
      if (stripePanel) stripePanel.classList.remove('hidden');
      wireStripePanel();
      void loadStripePanel();
    }

    // Profile + Venue + Appearance panels
    wireProfilePanel();
    wireVenuePanel();
    wireFrontDeskPanel();
    wireAllowedOriginsPanel();
    wireThemePanel();
    wireBrandingPanel();
    wireSpecialRequestsPanel();
    wireEmailTemplatesPanel();
    // Stripe Connect (Billing & Payouts) — Scale-only. Self-hides when the
    // tenant isn't on Scale (server-side gate also returns 402 in that case).
    wireConnectPanel();
  }

  // ---------------------------------------------------------------------------
  // Setup → Venue: Special Requests panel — per-tenant config for the booking
  // widget's free-text notes field. Backed by PUT /settings/special-requests
  // (owner/admin-gated). Defaults are loaded from /info (which returns the
  // tenant row, including the four special_requests_* columns from migration
  // 0041).
  // ---------------------------------------------------------------------------
  function wireSpecialRequestsPanel() {
    const panel = document.getElementById('special-requests-panel');
    const resolved = resolveTenantSlug();
    const slug = resolved?.slug;
    if (!panel || !slug) return;
    panel.classList.remove('hidden');

    const enabledEl = document.getElementById('sr-enabled') as HTMLInputElement | null;
    const labelEl = document.getElementById('sr-label') as HTMLInputElement | null;
    const placeholderEl = document.getElementById('sr-placeholder') as HTMLInputElement | null;
    const requiredEl = document.getElementById('sr-required') as HTMLInputElement | null;
    const saveBtn = document.getElementById('btn-save-special-requests');
    const statusEl = document.getElementById('sr-save-status');

    const api = tenantApi(slug);

    async function load() {
      try {
        const { tenant } = await api.info();
        const t = tenant as Record<string, unknown>;
        if (enabledEl) enabledEl.checked = (t.special_requests_enabled as number | undefined) !== 0;
        if (labelEl) labelEl.value = (t.special_requests_label as string | undefined) ?? 'Special requests';
        if (placeholderEl) placeholderEl.value = (t.special_requests_placeholder as string | undefined) ?? '';
        if (requiredEl) requiredEl.checked = (t.special_requests_required as number | undefined) === 1;
      } catch (err) {
        console.error('[special-requests] load failed', err);
      }
    }

    saveBtn?.addEventListener('click', async () => {
      if (!statusEl) return;
      statusEl.textContent = 'Saving…';
      try {
        await api.updateSpecialRequests({
          enabled: enabledEl?.checked ? 1 : 0,
          label: labelEl?.value.trim() || 'Special requests',
          placeholder: placeholderEl?.value ?? '',
          required: requiredEl?.checked ? 1 : 0,
        });
        statusEl.textContent = 'Saved.';
        await load();
      } catch (err) {
        statusEl.textContent = 'Save failed: ' + (err instanceof ApiError ? err.message : String(err));
      }
    });

    void load();
  }

  // ---------------------------------------------------------------------------
  // Setup → Venue: Email Templates panel — per-tenant overrides for the
  // dispatcher's hard-coded templates (booking_confirmation, admin_new_booking).
  // Null on any field reverts that field to the default. Backed by:
  //   GET    /email-templates              list keys + defaults + overrides
  //   PUT    /email-templates/{key}        upsert override
  //   DELETE /email-templates/{key}        revert to defaults
  // ---------------------------------------------------------------------------
  interface EmailTplDto {
    template_key: string;
    label: string;
    description: string;
    variables: string[];
    defaults: { subject: string; body_html: string; body_text: string };
    override: { subject: string | null; body_html: string | null; body_text: string | null } | null;
  }

  function wireEmailTemplatesPanel() {
    const panel = document.getElementById('email-templates-panel');
    const resolved = resolveTenantSlug();
    const slug = resolved?.slug;
    if (!panel || !slug) return;
    panel.classList.remove('hidden');

    const tplSelect = document.getElementById('et-template') as HTMLSelectElement | null;
    const subjectEl = document.getElementById('et-subject') as HTMLInputElement | null;
    const bodyHtmlEl = document.getElementById('et-body-html') as HTMLTextAreaElement | null;
    const bodyTextEl = document.getElementById('et-body-text') as HTMLTextAreaElement | null;
    const varsEl = document.getElementById('et-variables');
    const saveBtn = document.getElementById('btn-save-email-template');
    const revertBtn = document.getElementById('btn-revert-email-template');
    const statusEl = document.getElementById('et-save-status');

    const api = tenantApi(slug);
    let templates: EmailTplDto[] = [];

    function applySelection() {
      if (!tplSelect) return;
      const key = tplSelect.value;
      const t = templates.find((x) => x.template_key === key);
      if (!t) return;
      if (subjectEl) subjectEl.value = t.override?.subject ?? t.defaults.subject;
      if (bodyHtmlEl) bodyHtmlEl.value = t.override?.body_html ?? t.defaults.body_html;
      if (bodyTextEl) bodyTextEl.value = t.override?.body_text ?? t.defaults.body_text;
      if (varsEl) varsEl.textContent = t.variables.map((v) => '{{' + v + '}}').join('  ');
    }

    async function load() {
      try {
        const res = await api.listEmailTemplates();
        templates = res.templates;
        if (tplSelect) {
          tplSelect.innerHTML = '';
          for (const t of templates) {
            const opt = document.createElement('option');
            opt.value = t.template_key;
            opt.textContent = t.label;
            tplSelect.appendChild(opt);
          }
        }
        applySelection();
      } catch (err) {
        console.error('[email-templates] load failed', err);
      }
    }

    tplSelect?.addEventListener('change', applySelection);

    saveBtn?.addEventListener('click', async () => {
      if (!tplSelect || !statusEl) return;
      const key = tplSelect.value;
      statusEl.textContent = 'Saving…';
      try {
        await api.putEmailTemplate(key, {
          subject: subjectEl?.value ?? null,
          body_html: bodyHtmlEl?.value ?? null,
          body_text: bodyTextEl?.value ?? null,
        });
        statusEl.textContent = 'Saved override for ' + key + '.';
        await load();
      } catch (err) {
        statusEl.textContent = 'Save failed: ' + (err instanceof ApiError ? err.message : String(err));
      }
    });

    revertBtn?.addEventListener('click', async () => {
      if (!tplSelect || !statusEl) return;
      const key = tplSelect.value;
      statusEl.textContent = 'Reverting…';
      try {
        await api.deleteEmailTemplate(key);
        statusEl.textContent = 'Reverted ' + key + ' to default.';
        await load();
      } catch (err) {
        statusEl.textContent = 'Revert failed: ' + (err instanceof ApiError ? err.message : String(err));
      }
    });

    void load();
  }

  // ---------------------------------------------------------------------------
  // Setup → Venue: Widget Embed Domains (owner/admin only)
  // Self-service editor for `tenants.allowed_origins` — the JSON-encoded
  // string[] of hosts allowed to load the booking widget. Owner-gated by the
  // PATCH /info endpoint (apps/api/src/routes/admin/info.ts).
  // ---------------------------------------------------------------------------
  function wireAllowedOriginsPanel() {
    const panel = document.getElementById('allowed-origins-panel');
    if (!panel) return;
    if (!canEditVenue) return;
    panel.classList.remove('hidden');

    const list = document.getElementById('allowed-origins-list') as HTMLDivElement | null;
    const input = document.getElementById('allowed-origin-input') as HTMLInputElement | null;
    const addBtn = document.getElementById('btn-add-allowed-origin') as HTMLButtonElement | null;
    const status = document.getElementById('allowed-origins-status');
    if (!list || !input || !addBtn || !status) return;
    if (addBtn.getAttribute('data-wired') === '1') return;
    addBtn.setAttribute('data-wired', '1');

    // Parse the JSON-encoded array off the cached tenant row. Tolerate
    // null / empty-string / malformed JSON by falling back to [].
    function parseOrigins(raw: unknown): string[] {
      if (typeof raw !== 'string' || raw.length === 0) return [];
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.filter((v): v is string => typeof v === 'string' && v.length > 0);
        }
      } catch {
        /* malformed — treat as empty */
      }
      return [];
    }

    // Normalise an origin: trim, strip a single trailing slash, validate
    // that it parses as an http(s) URL. Returns null if invalid.
    function normaliseOrigin(raw: string): string | null {
      const trimmed = raw.trim();
      if (trimmed.length === 0) return null;
      if (!/^https?:\/\//i.test(trimmed)) return null;
      let u: URL;
      try {
        u = new URL(trimmed);
      } catch {
        return null;
      }
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
      // Strip trailing slash + any path/query/hash — we only keep scheme+host(+port).
      const port = u.port ? `:${u.port}` : '';
      return `${u.protocol}//${u.hostname}${port}`;
    }

    let origins: string[] = parseOrigins(tenant.allowed_origins);

    function render() {
      if (!list) return;
      if (origins.length === 0) {
        list.innerHTML =
          '<span class="field-hint" style="font-style:italic;">No domains added yet. Add the URL of any site allowed to embed your booking widget.</span>';
        return;
      }
      list.innerHTML = origins
        .map(
          (o, i) => `
            <span class="origin-chip" data-idx="${i}" style="display:inline-flex;align-items:center;gap:0.4rem;padding:0.25rem 0.6rem;border-radius:9999px;background:#eef2ff;color:#1e293b;font-size:0.875rem;border:1px solid #c7d2fe;">
              <code style="background:transparent;padding:0;font-size:0.85rem;">${escapeHtml(o)}</code>
              <button type="button" class="origin-chip-remove" data-idx="${i}" aria-label="Remove ${escapeHtml(o)}" style="background:none;border:none;cursor:pointer;color:#6366f1;font-size:1rem;line-height:1;padding:0 0.1rem;">&times;</button>
            </span>`,
        )
        .join('');
      list.querySelectorAll<HTMLButtonElement>('.origin-chip-remove').forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = Number(btn.getAttribute('data-idx') ?? '-1');
          if (idx < 0 || idx >= origins.length) return;
          const removed = origins[idx];
          const next = origins.slice(0, idx).concat(origins.slice(idx + 1));
          void save(next, `Removed ${removed}.`);
        });
      });
    }

    async function save(next: string[], successMsg: string) {
      if (!status) return;
      const prev = origins;
      status.textContent = 'Saving…';
      status.className = 'profile-status';
      try {
        const payload = next.length === 0 ? null : JSON.stringify(next);
        await api.updateTenantInfo({ allowed_origins: payload });
        origins = next;
        // Mirror into the cached tenant so a re-open of the panel reflects state.
        tenant.allowed_origins = payload;
        render();
        status.textContent = successMsg;
        status.className = 'profile-status ok';
      } catch (err) {
        // Roll back on failure so the chips reflect what's actually on the server.
        origins = prev;
        render();
        status.textContent = `Save failed: ${(err as Error).message}`;
        status.className = 'profile-status err';
      }
    }

    function tryAdd() {
      if (!input || !status) return;
      const raw = input.value;
      const normalised = normaliseOrigin(raw);
      if (!normalised) {
        status.textContent =
          'Enter a valid URL starting with http:// or https:// (e.g. https://example.com).';
        status.className = 'profile-status err';
        return;
      }
      if (origins.includes(normalised)) {
        status.textContent = `${normalised} is already on the list.`;
        status.className = 'profile-status err';
        return;
      }
      const next = origins.concat([normalised]);
      input.value = '';
      void save(next, `Added ${normalised}.`);
    }

    addBtn.addEventListener('click', tryAdd);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        tryAdd();
      }
    });

    render();
  }

  // ---------------------------------------------------------------------------
  // Setup → Profile: My Account (name/phone/notify)
  // ---------------------------------------------------------------------------
  function wireProfilePanel() {
    const nameEl = document.getElementById('profile-name') as HTMLInputElement | null;
    const emailEl = document.getElementById('profile-email') as HTMLInputElement | null;
    const phoneEl = document.getElementById('profile-phone') as HTMLInputElement | null;
    const notifyBooking = document.getElementById('profile-notify-booking') as HTMLInputElement | null;
    const notifyCancel = document.getElementById('profile-notify-cancel') as HTMLInputElement | null;
    const saveBtn = document.getElementById('btn-save-profile');
    const status = document.getElementById('profile-save-status');
    if (!nameEl || !emailEl || !phoneEl || !notifyBooking || !notifyCancel || !saveBtn || !status) return;
    if (saveBtn.getAttribute('data-wired') === '1') return;
    saveBtn.setAttribute('data-wired', '1');

    // Hydrate from the /me payload we already have.
    nameEl.value = me.user.name ?? '';
    emailEl.value = me.user.email ?? '';
    phoneEl.value = me.user.phone ?? '';
    notifyBooking.checked = (me.user.notify_on_booking ?? 1) === 1;
    notifyCancel.checked = (me.user.notify_on_cancellation ?? 1) === 1;

    saveBtn.addEventListener('click', async () => {
      status.textContent = 'Saving…';
      status.className = 'profile-status';
      try {
        await authApi.updateMe({
          name: nameEl.value.trim() || undefined,
          phone: phoneEl.value.trim() || null,
          notify_on_booking: notifyBooking.checked ? 1 : 0,
          notify_on_cancellation: notifyCancel.checked ? 1 : 0,
        });
        status.textContent = 'Saved.';
        status.className = 'profile-status ok';
      } catch (err) {
        status.textContent = `Save failed: ${(err as Error).message}`;
        status.className = 'profile-status err';
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Setup → Profile: Venue details (owner/admin only)
  // ---------------------------------------------------------------------------
  function wireVenuePanel() {
    const panel = document.getElementById('venue-panel');
    if (!panel) return;
    if (!canEditVenue) {
      // Keep panel hidden + skip wiring for staff-role users.
      return;
    }
    panel.classList.remove('hidden');
    const nameEl = document.getElementById('venue-name') as HTMLInputElement | null;
    const phoneEl = document.getElementById('venue-phone') as HTMLInputElement | null;
    const tzEl = document.getElementById('venue-timezone') as HTMLInputElement | null;
    const curEl = document.getElementById('venue-currency') as HTMLInputElement | null;
    const logoEl = document.getElementById('venue-logo-url') as HTMLInputElement | null;
    const preview = document.getElementById('venue-logo-preview') as HTMLImageElement | null;
    const previewName = document.getElementById('venue-logo-preview-name');
    const notifyEl = document.getElementById('venue-notification-email') as HTMLInputElement | null;
    const hideBrandingEl = document.getElementById('venue-hide-branding-footer') as HTMLInputElement | null;
    const saveBtn = document.getElementById('btn-save-venue');
    const status = document.getElementById('venue-save-status');
    if (!nameEl || !phoneEl || !tzEl || !curEl || !logoEl || !preview || !previewName || !saveBtn || !status) return;
    if (saveBtn.getAttribute('data-wired') === '1') return;
    saveBtn.setAttribute('data-wired', '1');

    // Hydrate from tenant info that bootApp already loaded.
    nameEl.value = (tenant.name as string | undefined) ?? '';
    phoneEl.value = (tenant.phone as string | undefined) ?? '';
    tzEl.value = (tenant.timezone as string | undefined) ?? '';
    curEl.value = (tenant.currency as string | undefined) ?? '';
    logoEl.value = (tenant.logo_url as string | undefined) ?? '';
    if (notifyEl) notifyEl.value = (tenant.notification_email as string | undefined) ?? '';
    if (hideBrandingEl) hideBrandingEl.checked = (tenant.hide_branding_footer as number | undefined) === 1;
    previewName.textContent = nameEl.value || 'Your Venue';
    if (logoEl.value) preview.setAttribute('src', logoEl.value);

    logoEl.addEventListener('input', () => {
      const v = logoEl.value.trim();
      if (v) preview.setAttribute('src', v);
      else preview.removeAttribute('src');
    });
    nameEl.addEventListener('input', () => {
      previewName.textContent = nameEl.value || 'Your Venue';
    });

    // File upload: read as base64 data URL (up to 200KB), write into logoEl
    // so the existing save flow persists it. R2-backed uploads come later.
    const fileInput = document.getElementById('venue-logo-file') as HTMLInputElement | null;
    const btnUpload = document.getElementById('btn-venue-logo-upload');
    const btnClear = document.getElementById('btn-venue-logo-clear');
    btnUpload?.addEventListener('click', () => fileInput?.click());
    btnClear?.addEventListener('click', () => {
      logoEl.value = '';
      logoEl.dispatchEvent(new Event('input'));
    });
    fileInput?.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      if (file.size > 200 * 1024) {
        status.textContent = `Image too large (${Math.round(file.size / 1024)} KB). Max 200 KB.`;
        status.className = 'profile-status err';
        fileInput.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : '';
        logoEl.value = dataUrl;
        logoEl.dispatchEvent(new Event('input'));
        status.textContent = 'Image ready — click Save to publish.';
        status.className = 'profile-status';
      };
      reader.onerror = () => {
        status.textContent = 'Could not read file.';
        status.className = 'profile-status err';
      };
      reader.readAsDataURL(file);
    });

    saveBtn.addEventListener('click', async () => {
      status.textContent = 'Saving…';
      status.className = 'profile-status';
      const body: Record<string, string | number | null> = {};
      if (nameEl.value.trim()) body.name = nameEl.value.trim();
      body.phone = phoneEl.value.trim() || null;
      if (tzEl.value.trim()) body.timezone = tzEl.value.trim();
      if (curEl.value.trim()) body.currency = curEl.value.trim().toUpperCase();
      body.logo_url = logoEl.value.trim() || null;
      if (hideBrandingEl) body.hide_branding_footer = hideBrandingEl.checked ? 1 : 0;

      // Validate notification emails — single email or comma-separated list.
      // Block save with inline error if any entry doesn't look like an email.
      if (notifyEl) {
        const raw = notifyEl.value.trim();
        if (raw === '') {
          body.notification_email = null;
        } else {
          const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const parts = raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
          const bad = parts.filter((p) => !re.test(p));
          if (bad.length > 0) {
            status.textContent = `Invalid email${bad.length > 1 ? 's' : ''}: ${bad.join(', ')}`;
            status.className = 'profile-status err';
            return;
          }
          body.notification_email = parts.join(', ');
        }
      }

      try {
        await api.updateTenantSettings(body);
        // Mirror into the local cached copy so any reopen of the panel is correct.
        Object.assign(tenant, body);
        // Update the header tenant logo/name live.
        renderHeaderTenant(tenant);
        status.textContent = 'Saved.';
        status.className = 'profile-status ok';
      } catch (err) {
        status.textContent = `Save failed: ${(err as Error).message}`;
        status.className = 'profile-status err';
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Setup → Front Desk Staff (owner/admin only)
  // Manages tenant_users where role='staff'. DB value stays 'staff' for
  // backwards compat — the UI just labels it "Front Desk".
  // ---------------------------------------------------------------------------
  function wireFrontDeskPanel() {
    const panel = document.getElementById('frontdesk-panel');
    if (!panel) return;
    if (!canEditVenue) {
      // Non-admin/owner — keep panel hidden, skip wiring.
      return;
    }
    // GET /staff is requirePlan('pro','scale') server-side. On a Starter tenant
    // the eager refresh() below would 402, and the global fetch interceptor
    // would pop a blocking upgrade modal over the whole Setup page. Self-hide
    // instead — same pattern as wireConnectPanel().
    const frontDeskPlan = (tenant as { plan?: string } | undefined)?.plan;
    if (frontDeskPlan !== 'pro' && frontDeskPlan !== 'scale') return;
    panel.classList.remove('hidden');

    const tbody = document.getElementById('frontdesk-staff-tbody') as HTMLTableSectionElement | null;
    const emailEl = document.getElementById('frontdesk-new-email') as HTMLInputElement | null;
    const nameEl = document.getElementById('frontdesk-new-name') as HTMLInputElement | null;
    const addBtn = document.getElementById('btn-add-frontdesk') as HTMLButtonElement | null;
    const status = document.getElementById('frontdesk-save-status');
    if (!tbody || !emailEl || !nameEl || !addBtn || !status) return;
    if (addBtn.getAttribute('data-wired') === '1') return;
    addBtn.setAttribute('data-wired', '1');

    async function refresh() {
      try {
        const { staff } = await api.listStaff();
        if (!tbody) return;
        if (staff.length === 0) {
          tbody.innerHTML = `<tr><td colspan="4" class="field-hint" style="text-align:center;padding:1rem;">No front desk staff yet. Add one below.</td></tr>`;
          return;
        }
        tbody.innerHTML = staff
          .map((s) => {
            const display = escapeHtml(s.name || s.email);
            const statusBadge =
              s.status === 'active'
                ? '<span style="color:#15803d;font-weight:600;">Active</span>'
                : '<span style="color:#a16207;font-weight:600;">Pending invite</span>';
            return `
              <tr data-user-id="${escapeHtml(s.user_id)}" data-email="${escapeHtml(s.email)}">
                <td>${display}</td>
                <td><code style="font-size:0.85rem;">${escapeHtml(s.email)}</code></td>
                <td>${statusBadge}</td>
                <td><button class="btn btn-ghost btn-sm frontdesk-revoke" type="button">Remove</button></td>
              </tr>`;
          })
          .join('');
        tbody.querySelectorAll<HTMLButtonElement>('.frontdesk-revoke').forEach((btn) => {
          btn.addEventListener('click', async () => {
            const tr = btn.closest('tr') as HTMLTableRowElement | null;
            if (!tr) return;
            const userId = tr.getAttribute('data-user-id') ?? '';
            const email = tr.getAttribute('data-email') ?? '';
            if (!confirm(`Remove ${email} as Front Desk Staff? They will lose access to this venue.`)) {
              return;
            }
            try {
              await api.revokeStaff(userId);
              if (status) {
                status.textContent = `Removed ${email}.`;
                status.className = 'profile-status ok';
              }
              await refresh();
            } catch (err) {
              if (status) {
                status.textContent = `Remove failed: ${(err as Error).message}`;
                status.className = 'profile-status err';
              }
            }
          });
        });
      } catch (err) {
        if (tbody) {
          tbody.innerHTML = `<tr><td colspan="4" class="profile-status err" style="text-align:center;padding:1rem;">Failed to load: ${escapeHtml(
            (err as Error).message,
          )}</td></tr>`;
        }
      }
    }

    addBtn.addEventListener('click', async () => {
      if (!status) return;
      const email = emailEl.value.trim().toLowerCase();
      const name = nameEl.value.trim();
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!re.test(email)) {
        status.textContent = 'Enter a valid email address.';
        status.className = 'profile-status err';
        return;
      }
      addBtn.disabled = true;
      status.textContent = 'Adding…';
      status.className = 'profile-status';
      try {
        const res = await api.inviteStaff({ email, name: name || null });
        status.textContent = res.message;
        status.className = 'profile-status ok';
        emailEl.value = '';
        nameEl.value = '';
        await refresh();
      } catch (err) {
        status.textContent = `Add failed: ${(err as Error).message}`;
        status.className = 'profile-status err';
      } finally {
        addBtn.disabled = false;
      }
    });

    void refresh();
  }

  // ---------------------------------------------------------------------------
  // Setup → Widget Branding panel — logo, colors, fonts, activity images.
  // Uses PATCH /branding for theme + font, the existing PUT /settings/tenant
  // for the logo, and PATCH /activities/:id for per-activity image_url.
  // ---------------------------------------------------------------------------
  function wireBrandingPanel() {
    const root = document.getElementById('setup-section-branding');
    if (!root) return;
    if (root.getAttribute('data-wired') === '1') return;
    root.setAttribute('data-wired', '1');

    // ---- Inner branding tabs (Logo | Colors | Font | Activity Images) ----
    // The sticky live preview stays pinned above; tabs only swap the control
    // panels below it. Save/Preview bar stays visible across all tabs.
    const brandTabs = root.querySelectorAll<HTMLButtonElement>('.brand-tab');
    const brandPanels = root.querySelectorAll<HTMLElement>('.brand-tabpanel');
    brandTabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const which = tab.getAttribute('data-brandtab');
        brandTabs.forEach((t) => t.classList.toggle('active', t === tab));
        brandPanels.forEach((p) =>
          p.classList.toggle('active', p.getAttribute('data-brandpanel') === which),
        );
      });
    });

    // ---- Hydrate theme + logo from cached tenant + parse theme JSON ----
    const themeRaw = (tenant.theme as string | null | undefined) ?? null;
    let themeObj: Record<string, string> = {};
    if (themeRaw) {
      try { themeObj = JSON.parse(themeRaw) as Record<string, string>; } catch { themeObj = {}; }
    }

    // Logo
    const logoUrlEl = document.getElementById('brand-logo-url') as HTMLInputElement | null;
    const logoPreview = document.getElementById('brand-logo-preview') as HTMLImageElement | null;
    const logoPreviewName = document.getElementById('brand-logo-preview-name');
    const logoFileInput = document.getElementById('brand-logo-file') as HTMLInputElement | null;
    const btnLogoUpload = document.getElementById('btn-brand-logo-upload');
    const btnLogoClear = document.getElementById('btn-brand-logo-clear');

    if (logoUrlEl && logoPreview && logoPreviewName) {
      logoUrlEl.value = (tenant.logo_url as string | undefined) ?? '';
      const venueName =
        (tenant.brand_name as string | undefined) ||
        (tenant.name as string | undefined) ||
        'Your Venue';
      logoPreviewName.textContent = venueName;
      if (logoUrlEl.value) logoPreview.setAttribute('src', logoUrlEl.value);
      // Hide the name once a logo is present, mirroring the real widget header.
      logoPreviewName.style.display = logoUrlEl.value ? 'none' : '';
      logoUrlEl.addEventListener('input', () => {
        const v = logoUrlEl.value.trim();
        if (v) {
          logoPreview.setAttribute('src', v);
          logoPreviewName.style.display = 'none';
        } else {
          logoPreview.setAttribute('src', '');
          logoPreviewName.style.display = '';
        }
      });
    }
    btnLogoUpload?.addEventListener('click', () => logoFileInput?.click());
    btnLogoClear?.addEventListener('click', () => {
      if (!logoUrlEl) return;
      logoUrlEl.value = '';
      logoUrlEl.dispatchEvent(new Event('input'));
    });
    logoFileInput?.addEventListener('change', () => {
      const file = logoFileInput.files?.[0];
      if (!file || !logoUrlEl) return;
      const status = document.getElementById('brand-save-status');
      if (file.size > 200 * 1024) {
        if (status) {
          status.textContent = `Image too large (${Math.round(file.size / 1024)} KB). Max 200 KB.`;
          status.className = 'profile-status err';
        }
        logoFileInput.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : '';
        logoUrlEl.value = dataUrl;
        logoUrlEl.dispatchEvent(new Event('input'));
        if (status) {
          status.textContent = 'Logo ready — click Save to publish.';
          status.className = 'profile-status';
        }
      };
      reader.readAsDataURL(file);
    });

    // ---- Color pickers (hex text + native picker stay in sync) ----
    const colorKeys: Array<{ key: string; defVal: string }> = [
      { key: 'primary', defVal: '#7C3AED' },
      { key: 'accent', defVal: '#A78BFA' },
      { key: 'bg', defVal: '#FFFFFF' },
      { key: 'bg-card', defVal: '#FFFFFF' },
      { key: 'text', defVal: '#0F172A' },
    ];
    function themeKey(k: string): string {
      // Convert dashed UI key → underscored DB key (bg-card → bg_card).
      return k.replace(/-/g, '_');
    }
    function getColorVal(k: string): string {
      const v = themeObj[themeKey(k)];
      return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v) ? v : '';
    }
    for (const { key, defVal } of colorKeys) {
      const text = document.getElementById(`brand-color-${key}`) as HTMLInputElement | null;
      const pick = document.getElementById(`brand-color-${key}-pick`) as HTMLInputElement | null;
      if (!text || !pick) continue;
      const current = getColorVal(key) || defVal;
      text.value = current;
      pick.value = current;
      pick.addEventListener('input', () => {
        text.value = pick.value.toUpperCase();
        updatePreview();
      });
      text.addEventListener('input', () => {
        const v = text.value.trim();
        if (/^#[0-9a-fA-F]{6}$/.test(v)) {
          pick.value = v;
          updatePreview();
        }
      });
    }

    // ---- Font picker ----
    const fontEl = document.getElementById('brand-font') as HTMLSelectElement | null;
    const fontPreview = document.getElementById('brand-font-preview') as HTMLElement | null;
    if (fontEl) {
      fontEl.value = (themeObj.font as string | undefined) ?? '';
      fontEl.addEventListener('change', () => updatePreview());
    }

    // Mirror the widget's crude luma check (theme.ts pickReadable) so the
    // preview button's text colour matches what the deployed widget renders.
    function pickReadable(hex: string): string {
      const m = /^#?([a-f\d]{6})$/i.exec(hex);
      if (!m) return '#ffffff';
      const n = parseInt(m[1], 16);
      const r = (n >> 16) & 0xff;
      const g = (n >> 8) & 0xff;
      const b = n & 0xff;
      const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luma > 0.6 ? '#0f172a' : '#ffffff';
    }

    // Drive the sticky live-preview frame entirely via CSS custom properties so
    // every control (colors, font, logo) reflects instantly, before Save. The
    // structure mirrors the real widget: logo bar → step header → body card +
    // primary button, themed the same way theme.ts applies them.
    function updatePreview() {
      const frame = document.getElementById('brand-widget-preview');
      const primary = (document.getElementById('brand-color-primary') as HTMLInputElement)?.value || '#7C3AED';
      const accent = (document.getElementById('brand-color-accent') as HTMLInputElement)?.value || '#A78BFA';
      const bg = (document.getElementById('brand-color-bg') as HTMLInputElement)?.value || '#FFFFFF';
      const bgCard = (document.getElementById('brand-color-bg-card') as HTMLInputElement)?.value || '#FFFFFF';
      const text = (document.getElementById('brand-color-text') as HTMLInputElement)?.value || '#0F172A';
      const font = fontEl?.value || '';
      const fontFamily = font ? `'${font}', system-ui, sans-serif` : "system-ui, sans-serif";

      if (frame) {
        frame.style.setProperty('--bp-primary', primary);
        frame.style.setProperty('--bp-primary-fg', pickReadable(primary));
        frame.style.setProperty('--bp-accent', accent);
        frame.style.setProperty('--bp-bg', bg);
        frame.style.setProperty('--bp-card', bgCard);
        frame.style.setProperty('--bp-text', text);
        frame.style.setProperty('--bp-font', fontFamily);
      }
      if (fontPreview) {
        fontPreview.style.fontFamily = fontFamily;
      }
    }
    updatePreview();

    // ---- Activity images list ----
    const listEl = document.getElementById('brand-activities-list');
    const activityImageDrafts: Record<string, string | null> = {}; // id → new image_url (null = clear)
    async function renderActivities() {
      if (!listEl) return;
      try {
        const resp = await api.activities();
        const all = (resp.activities as unknown as ApiActivity[]) ?? [];
        listEl.innerHTML = '';
        for (const a of all) {
          const row = document.createElement('div');
          row.style.cssText = 'display:flex; align-items:center; gap:0.75rem; padding:0.5rem; border:1px solid var(--border, #E2E8F0); border-radius:8px;';
          const draft = activityImageDrafts[a.id];
          const currentImg = draft !== undefined ? draft : (a.image_url ?? null);
          const thumb = document.createElement('div');
          thumb.style.cssText = 'width:64px; height:64px; border-radius:8px; background:#F1F5F9; display:flex; align-items:center; justify-content:center; font-size:28px; overflow:hidden; flex-shrink:0;';
          if (currentImg) {
            const img = document.createElement('img');
            img.src = currentImg;
            img.style.cssText = 'width:100%; height:100%; object-fit:cover;';
            thumb.appendChild(img);
          } else {
            thumb.textContent = a.icon ?? '🎯';
          }
          const label = document.createElement('div');
          label.style.cssText = 'flex:1 1 auto; font-weight:600;';
          label.textContent = a.label;
          const upload = document.createElement('button');
          upload.type = 'button';
          upload.className = 'btn btn-ghost';
          upload.textContent = currentImg ? 'Replace' : 'Upload';
          const clear = document.createElement('button');
          clear.type = 'button';
          clear.className = 'btn btn-ghost';
          clear.textContent = 'Remove';
          clear.style.display = currentImg ? '' : 'none';
          const fileIn = document.createElement('input');
          fileIn.type = 'file';
          fileIn.accept = 'image/png,image/jpeg,image/webp';
          fileIn.hidden = true;
          upload.addEventListener('click', () => fileIn.click());
          fileIn.addEventListener('change', () => {
            const file = fileIn.files?.[0];
            if (!file) return;
            const status = document.getElementById('brand-save-status');
            if (file.size > 200 * 1024) {
              if (status) {
                status.textContent = `Image too large (${Math.round(file.size / 1024)} KB). Max 200 KB.`;
                status.className = 'profile-status err';
              }
              fileIn.value = '';
              return;
            }
            const reader = new FileReader();
            reader.onload = () => {
              const dataUrl = typeof reader.result === 'string' ? reader.result : '';
              activityImageDrafts[a.id] = dataUrl;
              void renderActivities();
            };
            reader.readAsDataURL(file);
          });
          clear.addEventListener('click', () => {
            activityImageDrafts[a.id] = null;
            void renderActivities();
          });
          row.appendChild(thumb);
          row.appendChild(label);
          row.appendChild(upload);
          row.appendChild(clear);
          row.appendChild(fileIn);
          listEl.appendChild(row);
        }
      } catch (err) {
        listEl.innerHTML = `<div class="field-hint">Could not load activities: ${(err as Error).message}</div>`;
      }
    }
    void renderActivities();

    // ---- Save / Preview ----
    const saveBtn = document.getElementById('btn-brand-save');
    const previewBtn = document.getElementById('btn-brand-preview');
    const status = document.getElementById('brand-save-status');

    saveBtn?.addEventListener('click', async () => {
      if (status) {
        status.textContent = 'Saving…';
        status.className = 'profile-status';
      }
      try {
        // Logo via tenant settings
        const logoVal = (logoUrlEl?.value ?? '').trim() || null;
        if (logoVal !== ((tenant.logo_url as string | null | undefined) ?? null)) {
          await api.updateTenantSettings({ logo_url: logoVal });
          tenant.logo_url = logoVal;
          renderHeaderTenant(tenant);
        }
        // Theme + font via /branding
        const newTheme: Record<string, string> = {};
        for (const { key } of colorKeys) {
          const v = (document.getElementById(`brand-color-${key}`) as HTMLInputElement)?.value.trim();
          if (v && /^#[0-9a-fA-F]{6}$/.test(v)) newTheme[themeKey(key)] = v.toUpperCase();
        }
        const fontVal = fontEl?.value ?? '';
        if (fontVal) newTheme.font = fontVal;
        await api.updateBranding({ theme: newTheme });
        tenant.theme = JSON.stringify(newTheme);

        // Activity images via PATCH /activities/:id
        for (const [id, val] of Object.entries(activityImageDrafts)) {
          await api.updateActivity(id, { image_url: val });
        }
        // Drafts now reflect server state — clear so the next render reads fresh.
        for (const k of Object.keys(activityImageDrafts)) delete activityImageDrafts[k];

        if (status) {
          status.textContent = 'Saved.';
          status.className = 'profile-status ok';
        }
      } catch (err) {
        if (status) {
          status.textContent = `Save failed: ${(err as Error).message}`;
          status.className = 'profile-status err';
        }
      }
    });

    previewBtn?.addEventListener('click', () => {
      // Open the public widget for this tenant. The widget origin lives at
      // /widget/<slug> when served alongside the API; for local dev tenants
      // this is also where the demo iframe loads from.
      const url = `/widget/?tenant=${encodeURIComponent(slug)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    });
  }

  function renderHeaderTenant(t: Record<string, unknown>) {
    const sep = document.getElementById('tenant-sep');
    const img = document.getElementById('tenant-logo') as HTMLImageElement | null;
    const nm = document.getElementById('tenant-name');
    if (!sep || !img || !nm) return;
    const logoUrl = (t.logo_url as string | undefined) ?? '';
    const name = (t.name as string | undefined) ?? '';
    const hasTenantIdent = !!(logoUrl || name);
    sep.hidden = !hasTenantIdent;
    if (logoUrl) {
      img.src = logoUrl;
      img.alt = name;
      img.hidden = false;
      nm.textContent = '';
    } else {
      img.hidden = true;
      img.removeAttribute('src');
      nm.textContent = name;
    }
  }

  // ---------------------------------------------------------------------------
  // Setup → Profile: Appearance (theme picker)
  // ---------------------------------------------------------------------------
  function wireThemePanel() {
    const grid = document.getElementById('theme-grid');
    if (!grid) return;
    if (grid.getAttribute('data-wired') === '1') return;
    grid.setAttribute('data-wired', '1');

    const saveBtn = document.getElementById('btn-save-appearance');
    const status = document.getElementById('appearance-save-status');
    // `savedTheme` tracks what's persisted on the server. `pendingTheme` is
    // what the user previewed but hasn't saved yet. Preview applies instantly;
    // Save commits it to the server.
    let savedTheme = (localStorage.getItem('lanebooker_admin_theme') ?? 'light-blue');
    let pendingTheme = savedTheme;

    grid.querySelectorAll<HTMLButtonElement>('.theme-swatch').forEach((btn) => {
      if (btn.dataset.theme === savedTheme) btn.classList.add('selected');
      btn.addEventListener('click', () => {
        const theme = btn.dataset.theme || 'light-blue';
        grid.querySelectorAll('.theme-swatch').forEach((b) => b.classList.remove('selected'));
        btn.classList.add('selected');
        previewAdminTheme(theme);
        pendingTheme = theme;
        if (status) {
          status.textContent = theme === savedTheme ? '' : 'Unsaved changes.';
          status.className = theme === savedTheme ? 'profile-status' : 'profile-status';
        }
      });
    });

    saveBtn?.addEventListener('click', async () => {
      if (!status) return;
      status.textContent = 'Saving…';
      status.className = 'profile-status';
      try {
        await authApi.updateMe({ admin_theme: pendingTheme });
        savedTheme = pendingTheme;
        localStorage.setItem('lanebooker_admin_theme', savedTheme);
        status.textContent = 'Saved.';
        status.className = 'profile-status ok';
      } catch (err) {
        status.textContent = `Save failed: ${(err as Error).message}`;
        status.className = 'profile-status err';
      }
    });
  }

  function wireStripePanel() {
    const btn = document.getElementById('btn-save-stripe');
    if (!btn || btn.getAttribute('data-wired') === '1') return;
    btn.setAttribute('data-wired', '1');
    btn.addEventListener('click', () => void saveStripePanel());
  }

  async function loadStripePanel() {
    try {
      const res = await api.getStripeConfig();
      (document.getElementById('stripe-enabled') as HTMLInputElement).checked = res.enabled;
      (document.getElementById('stripe-pk') as HTMLInputElement).value = res.publishable_key ?? '';
      const payVenue = document.getElementById('allow-pay-at-venue') as HTMLInputElement | null;
      if (payVenue) payVenue.checked = !!res.allow_pay_at_venue;
      const sk = document.getElementById('stripe-sk') as HTMLInputElement;
      sk.value = '';
      sk.placeholder = res.secret_key_set ? '•••••••••••••••••••• (stored — leave blank to keep)' : 'sk_live_... or sk_test_...';
      const skStatus = document.getElementById('stripe-sk-status')!;
      skStatus.textContent = res.secret_key_set ? 'A secret key is stored. Leave blank to keep it unchanged.' : 'No secret key stored.';
      document.getElementById('stripe-save-status')!.textContent = '';
    } catch (err) {
      console.error('[admin] stripe load failed', err);
    }
  }

  async function saveStripePanel() {
    const enabled = (document.getElementById('stripe-enabled') as HTMLInputElement).checked;
    const pk = (document.getElementById('stripe-pk') as HTMLInputElement).value.trim();
    const sk = (document.getElementById('stripe-sk') as HTMLInputElement).value.trim();
    const payVenueEl = document.getElementById('allow-pay-at-venue') as HTMLInputElement | null;
    const allowPayAtVenue = payVenueEl ? payVenueEl.checked : undefined;
    const status = document.getElementById('stripe-save-status')!;
    status.textContent = 'Saving…';
    status.style.color = 'var(--text-muted)';
    try {
      await api.updateStripeConfig({
        enabled,
        publishable_key: pk || null,
        secret_key: sk ? sk : undefined,
        allow_pay_at_venue: allowPayAtVenue,
      });
      status.textContent = 'Saved.';
      status.style.color = 'var(--success, #16a34a)';
      await loadStripePanel();
    } catch (err) {
      status.textContent = `Save failed: ${(err as Error).message}`;
      status.style.color = 'var(--danger, #dc2626)';
    }
  }

  // ---------------------------------------------------------------------------
  // Billing & Payouts — Stripe Connect (Scale plan only).
  //
  // Connect is REQUIRED for Scale tenants: the public booking widget will
  // refuse paid bookings (503 connect_not_ready) until onboarding finishes.
  // LaneBooker takes 0% application fee — tenants keep 100% of booking revenue.
  // ---------------------------------------------------------------------------
  function wireConnectPanel() {
    const panel = document.getElementById('connect-panel');
    if (!panel) return;
    // Only show for Scale tenants. The outer `tenant` variable (loaded from
    // api.info() at boot) carries the plan. Plan-gating server-side also
    // returns 402, but hiding the UI keeps non-Scale tenants from seeing a
    // dead surface.
    const planVal = (tenant as { plan?: string } | undefined)?.plan;
    if (planVal !== 'scale') return;
    panel.classList.remove('hidden');

    const startBtn = document.getElementById('btn-connect-start');
    const continueBtn = document.getElementById('btn-connect-continue');
    const refreshBtn = document.getElementById('btn-connect-refresh');
    const dashboardBtn = document.getElementById('btn-connect-dashboard');

    startBtn?.addEventListener('click', () => void startConnectOnboarding());
    continueBtn?.addEventListener('click', () => void startConnectOnboarding());
    refreshBtn?.addEventListener('click', () => void loadConnectPanel());
    dashboardBtn?.addEventListener('click', () => void openConnectDashboard());

    // If we just came back from a Stripe onboarding redirect, re-fetch.
    const url = new URL(window.location.href);
    const connectParam = url.searchParams.get('connect');
    if (connectParam === 'return' || connectParam === 'refresh') {
      url.searchParams.delete('connect');
      window.history.replaceState({}, '', url.toString());
    }
    void loadConnectPanel();
  }

  async function loadConnectPanel() {
    const noneEl = document.getElementById('connect-state-none');
    const pendingEl = document.getElementById('connect-state-pending');
    const activeEl = document.getElementById('connect-state-active');
    const msg = document.getElementById('connect-status-msg');
    if (!noneEl || !pendingEl || !activeEl || !msg) return;
    msg.textContent = 'Loading…';
    msg.style.color = 'var(--text-muted)';
    noneEl.classList.add('hidden');
    pendingEl.classList.add('hidden');
    activeEl.classList.add('hidden');

    try {
      const s = await api.connectStatus();
      msg.textContent = '';
      if (s.status === 'none' || !s.account_id) {
        noneEl.classList.remove('hidden');
        return;
      }
      if (s.status === 'active') {
        activeEl.classList.remove('hidden');
        (document.getElementById('connect-charges-flag') as HTMLElement).textContent = s.charges_enabled ? 'enabled' : 'disabled';
        (document.getElementById('connect-payouts-flag') as HTMLElement).textContent = s.payouts_enabled ? 'enabled' : 'disabled';
        (document.getElementById('connect-account-id') as HTMLElement).textContent = s.account_id;
        // Fetch balance on the side.
        try {
          const b = await api.connectBalance();
          (document.getElementById('connect-balance-available') as HTMLElement).textContent = formatBalanceList(b.available);
          (document.getElementById('connect-balance-pending') as HTMLElement).textContent = formatBalanceList(b.pending);
        } catch (err) {
          (document.getElementById('connect-balance-available') as HTMLElement).textContent = '—';
          (document.getElementById('connect-balance-pending') as HTMLElement).textContent = `(balance unavailable: ${(err as Error).message})`;
        }
        return;
      }
      // pending or restricted
      pendingEl.classList.remove('hidden');
      (document.getElementById('connect-pending-status') as HTMLElement).textContent = s.status;
      const list = document.getElementById('connect-requirements-list') as HTMLElement;
      list.innerHTML = '';
      if (s.requirements && s.requirements.length > 0) {
        for (const r of s.requirements) {
          const li = document.createElement('li');
          li.textContent = r;
          list.appendChild(li);
        }
      } else {
        const li = document.createElement('li');
        li.textContent = 'Stripe is still reviewing your submission. Check back shortly.';
        list.appendChild(li);
      }
      const disabledEl = document.getElementById('connect-disabled-reason');
      if (disabledEl) {
        if (s.disabled_reason) {
          disabledEl.classList.remove('hidden');
          disabledEl.textContent = `Disabled reason: ${s.disabled_reason}`;
        } else {
          disabledEl.classList.add('hidden');
          disabledEl.textContent = '';
        }
      }
    } catch (err) {
      msg.textContent = `Failed to load Connect status: ${(err as Error).message}`;
      msg.style.color = 'var(--danger, #dc2626)';
    }
  }

  function formatBalanceList(items: Array<{ amount: number; currency: string }>): string {
    if (!items || items.length === 0) return '$0.00';
    return items
      .map((i) => {
        const amount = (i.amount / 100).toFixed(2);
        return `${amount} ${i.currency.toUpperCase()}`;
      })
      .join(', ');
  }

  async function startConnectOnboarding() {
    const msg = document.getElementById('connect-status-msg');
    if (msg) {
      msg.textContent = 'Generating onboarding link…';
      msg.style.color = 'var(--text-muted)';
    }
    try {
      const { url } = await api.connectOnboardingLink();
      window.location.href = url;
    } catch (err) {
      if (msg) {
        msg.textContent = `Onboarding link failed: ${(err as Error).message}`;
        msg.style.color = 'var(--danger, #dc2626)';
      }
    }
  }

  async function openConnectDashboard() {
    const msg = document.getElementById('connect-status-msg');
    if (msg) {
      msg.textContent = 'Opening Stripe dashboard…';
      msg.style.color = 'var(--text-muted)';
    }
    try {
      const { url } = await api.connectDashboardLink();
      window.open(url, '_blank', 'noopener,noreferrer');
      if (msg) msg.textContent = '';
    } catch (err) {
      if (msg) {
        msg.textContent = `Dashboard link failed: ${(err as Error).message}`;
        msg.style.color = 'var(--danger, #dc2626)';
      }
    }
  }

  async function reloadSetupData() {
    try {
      const [res, ad, pk, pr, act, ev] = await Promise.all([
        api.resources(),
        api.addons(),
        api.packages(),
        api.pricing(),
        api.activities(),
        api.events(),
      ]);
      setupResources = res.resources as unknown as ApiResource[];
      setupAddons = ad.addons as unknown as ApiAddon[];
      setupPackages = pk.packages as unknown as ApiPackage[];
      setupPricing = pr.rules as unknown as PricingRule[];
      setupEvents = ev.events as unknown as EventRec[];
      // Refresh the cached activity list (Setup may add activities in future)
      activities = (act.activities as unknown as ApiActivity[]).filter((a) => a.enabled);
      activityById.clear();
      activities.forEach((a) => activityById.set(a.id, a));
      renderSetupResourcesTable();
      renderSetupAddonsTable();
      renderSetupPackagesTable();
      renderSetupPricingTable();
      renderSetupEventsTable();
    } catch (err) {
      console.error('[admin] setup data load failed', err);
    }
  }

  function renderSetupResourcesTable() {
    const tbody = document.getElementById('setup-resources-tbody')!;
    if (!setupResources.length) {
      tbody.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:1.25rem;color:var(--text-muted)">No lanes yet.</td></tr>';
      return;
    }
    tbody.innerHTML = setupResources
      .sort((a, b) => a.activity_id.localeCompare(b.activity_id) || a.number - b.number)
      .map((r) => {
        const act = activityById.get(r.activity_id);
        return `<tr data-id="${r.id}">
        <td>${esc(act?.label || '—')}</td>
        <td>${r.number}</td>
        <td>${esc(r.label || '')}</td>
        <td>${esc(r.status)}</td>
        <td>
          <button class="cust-action-btn" data-act="edit" data-id="${r.id}">Edit</button>
          <button class="cust-action-btn" data-act="disable" data-id="${r.id}">Disable</button>
          <button class="cust-action-btn danger" data-act="delete" data-id="${r.id}">Delete</button>
        </td>
      </tr>`;
      })
      .join('');
    tbody.querySelectorAll('button').forEach((b) => {
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (b as HTMLElement).dataset.id!;
        const action = (b as HTMLElement).dataset.act!;
        const r = setupResources.find((x) => x.id === id);
        if (!r) return;
        if (action === 'edit') openResourceModal(r);
        else if (action === 'disable') void disableSetupEntity('resource', id);
        else if (action === 'delete') deleteSetupEntity('resource', id, r.label || `Lane ${r.number}`);
      });
    });
  }

  function renderSetupAddonsTable() {
    const tbody = document.getElementById('setup-addons-tbody')!;
    if (!setupAddons.length) {
      tbody.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:1.25rem;color:var(--text-muted)">No add-ons yet.</td></tr>';
      return;
    }
    tbody.innerHTML = setupAddons
      .map((a) => {
        return `<tr data-id="${a.id}">
        <td>${esc(a.label)}</td>
        <td>${esc(a.category || '—')}</td>
        <td>$${(a.price_cents / 100).toFixed(2)}</td>
        <td>${a.per_person ? 'Yes' : 'No'}</td>
        <td>${(a as unknown as { featured?: number }).featured ? 'Yes' : 'No'}</td>
        <td>${(a as unknown as { inventory: number | null }).inventory ?? '—'}</td>
        <td>${a.enabled ? 'Yes' : 'No'}</td>
        <td>
          <button class="cust-action-btn" data-act="edit" data-id="${a.id}">Edit</button>
          <button class="cust-action-btn" data-act="disable" data-id="${a.id}">Disable</button>
          <button class="cust-action-btn danger" data-act="delete" data-id="${a.id}">Delete</button>
        </td>
      </tr>`;
      })
      .join('');
    tbody.querySelectorAll('button').forEach((b) => {
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (b as HTMLElement).dataset.id!;
        const action = (b as HTMLElement).dataset.act!;
        const a = setupAddons.find((x) => x.id === id);
        if (!a) return;
        if (action === 'edit') openAddonModal(a);
        else if (action === 'disable') void disableSetupEntity('addon', id);
        else if (action === 'delete') deleteSetupEntity('addon', id, a.label);
      });
    });
  }

  function renderSetupPackagesTable() {
    const tbody = document.getElementById('setup-packages-tbody')!;
    if (!setupPackages.length) {
      tbody.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:1.25rem;color:var(--text-muted)">No packages yet.</td></tr>';
      return;
    }
    tbody.innerHTML = setupPackages
      .map((p) => {
        return `<tr data-id="${p.id}">
        <td>${esc(p.label)}</td>
        <td>${esc(p.description || '')}</td>
        <td>$${(p.price_cents / 100).toFixed(2)}</td>
        <td>${p.enabled ? 'Yes' : 'No'}</td>
        <td>
          <button class="cust-action-btn" data-act="edit" data-id="${p.id}">Edit</button>
          <button class="cust-action-btn" data-act="disable" data-id="${p.id}">Disable</button>
          <button class="cust-action-btn danger" data-act="delete" data-id="${p.id}">Delete</button>
        </td>
      </tr>`;
      })
      .join('');
    tbody.querySelectorAll('button').forEach((b) => {
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (b as HTMLElement).dataset.id!;
        const action = (b as HTMLElement).dataset.act!;
        const p = setupPackages.find((x) => x.id === id);
        if (!p) return;
        if (action === 'edit') openPackageModal(p);
        else if (action === 'disable') void disableSetupEntity('package', id);
        else if (action === 'delete') deleteSetupEntity('package', id, p.label);
      });
    });
  }

  function renderSetupPricingTable() {
    const tbody = document.getElementById('setup-pricing-tbody')!;
    if (!setupPricing.length) {
      tbody.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:1.25rem;color:var(--text-muted)">No pricing rules yet.</td></tr>';
      return;
    }
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    tbody.innerHTML = setupPricing
      .map((r) => {
        const act = activityById.get(r.activity_id);
        const day = r.day_of_week === null ? 'Any' : days[r.day_of_week];
        const window =
          r.start_time && r.end_time ? `${r.start_time}–${r.end_time}` : r.start_time || r.end_time || '—';
        const hourCell = r.hour_index == null ? 'Any' : String(r.hour_index);
        return `<tr data-id="${r.id}">
        <td>${esc(act?.label || '—')}</td>
        <td>${esc(r.label || '—')}</td>
        <td>${day}</td>
        <td>${window}</td>
        <td>${esc(r.rate_type)}</td>
        <td>$${(r.rate_cents / 100).toFixed(2)}</td>
        <td>${hourCell}</td>
        <td>
          <button class="cust-action-btn" data-act="edit" data-id="${r.id}">Edit</button>
          <button class="cust-action-btn danger" data-act="delete" data-id="${r.id}">Delete</button>
        </td>
      </tr>`;
      })
      .join('');
    tbody.querySelectorAll('button').forEach((b) => {
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (b as HTMLElement).dataset.id!;
        const action = (b as HTMLElement).dataset.act!;
        const r = setupPricing.find((x) => x.id === id);
        if (!r) return;
        if (action === 'edit') openPricingModal(r);
        else if (action === 'delete') deleteSetupEntity('pricing_rule', id, r.label || r.rate_type);
      });
    });
  }

  function renderSetupEventsTable() {
    const tbody = document.getElementById('setup-events-tbody')!;
    if (!setupEvents.length) {
      tbody.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:1.25rem;color:var(--text-muted)">No events yet.</td></tr>';
      return;
    }
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    tbody.innerHTML = setupEvents
      .map((e) => {
        const rec = parseRecurrence(e.recurrence);
        const days = rec ? rec.days.map((d) => dayNames[d] ?? '?').join(', ') : '—';
        const start = rec?.start ?? '—';
        const end = rec?.end ?? '—';
        const color = e.theme_color || '#a855f7';
        const swatch = `<span style="display:inline-block;width:14px;height:14px;border-radius:3px;vertical-align:middle;background:${esc(color)};margin-right:0.4rem;border:1px solid rgba(0,0,0,0.15)"></span>${esc(color)}`;
        return `<tr data-id="${e.id}">
          <td>${esc(e.name)}</td>
          <td>${esc(days)}</td>
          <td>${esc(start)}</td>
          <td>${esc(end)}</td>
          <td>${swatch}</td>
          <td>${e.enabled ? 'Yes' : 'No'}</td>
          <td>
            <button class="cust-action-btn" data-act="edit" data-id="${e.id}">Edit</button>
            <button class="cust-action-btn" data-act="delete" data-id="${e.id}">Delete</button>
          </td>
        </tr>`;
      })
      .join('');
    tbody.querySelectorAll('button').forEach((b) => {
      b.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const id = (b as HTMLElement).dataset.id!;
        const action = (b as HTMLElement).dataset.act!;
        const e = setupEvents.find((x) => x.id === id);
        if (!e) return;
        if (action === 'edit') openEventModal(e);
        else if (action === 'delete') void deleteEvent(id);
      });
    });
  }

  function openEventModal(e: EventRec | null) {
    const isEdit = !!e;
    const rec = parseRecurrence(e?.recurrence ?? null);
    const selectedDays = new Set(rec?.days ?? []);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const title = isEdit ? 'Edit Event' : 'Add Event';
    document.getElementById('setup-modal-title')!.textContent = title;
    const form = document.getElementById('setup-form') as HTMLFormElement;

    const dayCheckboxes = dayNames
      .map(
        (dn, i) =>
          `<label style="display:inline-flex;align-items:center;gap:0.25rem;margin-right:0.6rem;font-weight:500">
            <input type="checkbox" name="day-${i}" ${selectedDays.has(i) ? 'checked' : ''}> ${dn}
          </label>`,
      )
      .join('');

    form.innerHTML = `
      <div class="form-group">
        <label for="sf-name">Name</label>
        <input type="text" id="sf-name" name="name" value="${esc(e?.name ?? '')}" required>
      </div>
      <div class="form-group">
        <label>Days of week</label>
        <div style="display:flex;flex-wrap:wrap">${dayCheckboxes}</div>
      </div>
      <div class="form-group">
        <label for="sf-start">Start time (HH:MM)</label>
        <input type="time" id="sf-start" name="start" value="${esc(rec?.start ?? '19:00')}" required>
      </div>
      <div class="form-group">
        <label for="sf-end">End time (HH:MM)</label>
        <input type="time" id="sf-end" name="end" value="${esc(rec?.end ?? '23:30')}" required>
      </div>
      <div class="form-group">
        <label for="sf-color">Color</label>
        <input type="color" id="sf-color" name="theme_color" value="${esc(e?.theme_color || '#a855f7')}">
      </div>
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:0.5rem;font-weight:500">
          <input type="checkbox" id="sf-enabled" name="enabled" ${(e?.enabled ?? 1) ? 'checked' : ''}>
          Enabled
        </label>
      </div>
    `;

    const saveBtn = document.getElementById('setup-modal-save') as HTMLButtonElement;
    const newBtn = saveBtn.cloneNode(true) as HTMLButtonElement;
    saveBtn.parentNode!.replaceChild(newBtn, saveBtn);
    newBtn.addEventListener('click', async () => {
      const name = (form.querySelector('[name="name"]') as HTMLInputElement).value.trim();
      const start = (form.querySelector('[name="start"]') as HTMLInputElement).value;
      const end = (form.querySelector('[name="end"]') as HTMLInputElement).value;
      const color = (form.querySelector('[name="theme_color"]') as HTMLInputElement).value;
      const enabled = (form.querySelector('[name="enabled"]') as HTMLInputElement).checked ? 1 : 0;
      const days: number[] = [];
      for (let i = 0; i < 7; i++) {
        const cb = form.querySelector(`[name="day-${i}"]`) as HTMLInputElement | null;
        if (cb?.checked) days.push(i);
      }
      if (!name) {
        alert('Name is required');
        return;
      }
      if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) {
        alert('Start and end must be HH:MM');
        return;
      }
      const body: Record<string, unknown> = {
        name,
        recurrence: { days, start, end },
        theme_color: color || null,
        enabled,
      };
      try {
        newBtn.disabled = true;
        newBtn.textContent = 'Saving…';
        if (isEdit) await api.updateEvent(e!.id, body);
        else await api.createEvent(body);
        await reloadSetupData();
        await loadAuditLog();
        closeModal('setup-modal-overlay');
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : 'Save failed';
        alert(msg);
      } finally {
        newBtn.disabled = false;
        newBtn.textContent = 'Save';
      }
    });
    openModal('setup-modal-overlay');
  }

  async function deleteEvent(id: string) {
    if (!confirm('Delete this event?')) return;
    try {
      await api.deleteEvent(id);
      await reloadSetupData();
      await loadAuditLog();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Delete failed';
      alert(msg);
    }
  }

  // --- Generic setup modal driver ---------------------------------------------
  interface SetupFieldDef {
    name: string;
    label: string;
    type: 'text' | 'number' | 'select' | 'checkbox' | 'textarea' | 'dollars' | 'date';
    value?: string | number | boolean | null;
    options?: Array<{ value: string; label: string }>;
    required?: boolean;
    placeholder?: string;
    min?: number;
    max?: number;
    step?: string;
  }

  interface SetupModalOptions {
    onDelete?: () => Promise<void>;
    deleteConfirmMsg?: string;
  }

  function openSetupModal(
    title: string,
    fields: SetupFieldDef[],
    onSave: (values: Record<string, string | number | boolean>) => Promise<void>,
    opts: SetupModalOptions = {},
  ) {
    document.getElementById('setup-modal-title')!.textContent = title;
    const form = document.getElementById('setup-form') as HTMLFormElement;
    form.innerHTML = fields
      .map((f) => {
        const id = `sf-${f.name}`;
        const val = f.value ?? '';
        if (f.type === 'select') {
          return `<div class="form-group">
          <label for="${id}">${esc(f.label)}</label>
          <select id="${id}" name="${f.name}" ${f.required ? 'required' : ''}>
            ${f.options!.map((o) => `<option value="${esc(o.value)}" ${String(o.value) === String(val) ? 'selected' : ''}>${esc(o.label)}</option>`).join('')}
          </select>
        </div>`;
        }
        if (f.type === 'checkbox') {
          return `<div class="modal-check-row">
            <input type="checkbox" id="${id}" name="${f.name}" ${val ? 'checked' : ''}>
            <label for="${id}">${esc(f.label)}</label>
          </div>`;
        }
        if (f.type === 'textarea') {
          return `<div class="form-group">
          <label for="${id}">${esc(f.label)}</label>
          <textarea id="${id}" name="${f.name}" rows="3">${esc(String(val))}</textarea>
        </div>`;
        }
        if (f.type === 'dollars') {
          // Convert cents → dollars for display
          const cents = typeof val === 'number' ? val : (val === '' ? 0 : Number(val));
          const dollars = (cents / 100).toFixed(2);
          return `<div class="form-group">
            <label for="${id}">${esc(f.label)}</label>
            <div class="modal-dollar-input">
              <input type="number" id="${id}" name="${f.name}" value="${esc(dollars)}" step="0.01" min="0" placeholder="${esc(f.placeholder || '0.00')}" ${f.required ? 'required' : ''}>
            </div>
          </div>`;
        }
        const minAttr = f.min !== undefined ? `min="${f.min}"` : '';
        const maxAttr = f.max !== undefined ? `max="${f.max}"` : '';
        const stepAttr = f.step ? `step="${f.step}"` : '';
        return `<div class="form-group">
        <label for="${id}">${esc(f.label)}</label>
        <input type="${f.type}" id="${id}" name="${f.name}" value="${esc(String(val))}" placeholder="${esc(f.placeholder || '')}" ${minAttr} ${maxAttr} ${stepAttr} ${f.required ? 'required' : ''}>
      </div>`;
      })
      .join('');

    const saveBtn = document.getElementById('setup-modal-save') as HTMLButtonElement;
    const newBtn = saveBtn.cloneNode(true) as HTMLButtonElement;
    saveBtn.parentNode!.replaceChild(newBtn, saveBtn);
    newBtn.addEventListener('click', async () => {
      const values: Record<string, string | number | boolean> = {};
      for (const f of fields) {
        const input = form.querySelector(`[name="${f.name}"]`) as HTMLInputElement | null;
        if (!input) continue;
        if (f.type === 'checkbox') values[f.name] = input.checked;
        else if (f.type === 'dollars') {
          // Convert dollars → cents
          const dollars = input.value === '' ? 0 : Number(input.value);
          values[f.name] = Math.round(dollars * 100);
        } else if (f.type === 'number') values[f.name] = input.value === '' ? 0 : Number(input.value);
        else values[f.name] = input.value;
      }
      try {
        newBtn.disabled = true;
        newBtn.textContent = 'Saving…';
        await onSave(values);
        await reloadSetupData();
        await loadAuditLog();
        closeModal('setup-modal-overlay');
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : 'Save failed';
        alert(msg);
      } finally {
        newBtn.disabled = false;
        newBtn.textContent = 'Save';
      }
    });

    // Wire up Delete button (visible only when onDelete passed — i.e. edit mode)
    const delBtn = document.getElementById('setup-modal-delete') as HTMLButtonElement;
    const newDelBtn = delBtn.cloneNode(true) as HTMLButtonElement;
    delBtn.parentNode!.replaceChild(newDelBtn, delBtn);
    if (opts.onDelete) {
      newDelBtn.classList.remove('hidden');
      newDelBtn.addEventListener('click', () => {
        showSetupConfirm(opts.deleteConfirmMsg || 'Delete this item? This cannot be undone.', async () => {
          try {
            await opts.onDelete!();
            closeModal('setup-modal-overlay');
          } catch (err) {
            const msg = err instanceof ApiError ? err.message : 'Delete failed';
            alert(msg);
          }
        });
      });
    } else {
      newDelBtn.classList.add('hidden');
    }

    openModal('setup-modal-overlay');
  }

  // Custom delete-confirm modal (replaces window.confirm for Setup tab)
  function showSetupConfirm(message: string, onConfirm: () => Promise<void> | void) {
    const overlay = document.getElementById('setup-confirm-overlay')!;
    const msgEl = document.getElementById('setup-confirm-msg')!;
    msgEl.textContent = message;
    const yesBtn = document.getElementById('setup-confirm-yes') as HTMLButtonElement;
    const noBtn = document.getElementById('setup-confirm-no') as HTMLButtonElement;
    // Reset listeners
    const newYes = yesBtn.cloneNode(true) as HTMLButtonElement;
    yesBtn.parentNode!.replaceChild(newYes, yesBtn);
    const newNo = noBtn.cloneNode(true) as HTMLButtonElement;
    noBtn.parentNode!.replaceChild(newNo, noBtn);
    newNo.addEventListener('click', () => closeModal('setup-confirm-overlay'));
    newYes.addEventListener('click', async () => {
      newYes.disabled = true;
      newYes.textContent = 'Deleting…';
      try {
        await onConfirm();
      } finally {
        newYes.disabled = false;
        newYes.textContent = 'Delete';
        closeModal('setup-confirm-overlay');
      }
    });
    openModal('setup-confirm-overlay');
  }

  function openResourceModal(r: ApiResource | null) {
    const isEdit = !!r;
    // Sentinel value used in the Activity dropdown to signal "create a brand
    // new activity from this modal". When picked, two extra inputs (custom
    // name + image upload) are revealed below the dropdown.
    const OTHER = '__other__';
    const activityOptions: Array<{ value: string; label: string }> = [
      ...activities.map((a) => ({ value: a.id, label: a.label })),
    ];
    // Only offer "Other" when adding — editing an existing resource shouldn't
    // also create a new activity in the same step.
    if (!isEdit) activityOptions.push({ value: OTHER, label: 'Other (create new activity…)' });

    const fields: SetupFieldDef[] = [
      {
        name: 'activity_id',
        label: 'Activity',
        type: 'select',
        required: true,
        value: r?.activity_id ?? activities[0]?.id ?? '',
        options: activityOptions,
      },
      { name: 'number', label: 'Resource Number', type: 'number', required: true, min: 1, value: r?.number ?? 1 },
      { name: 'label', label: 'Label (optional)', type: 'text', value: r?.label ?? '' },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        value: r?.status ?? 'active',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'maintenance', label: 'Maintenance' },
          { value: 'inactive', label: 'Inactive' },
        ],
      },
    ];
    openSetupModal(
      isEdit ? 'Edit Resource' : 'Add Resource',
      fields,
      async (values) => {
        let activityId = String(values.activity_id);

        // "Other" path — first create the new activity, then use its id when
        // creating the resource. The custom name + image inputs live outside
        // the SetupFieldDef list so we read them straight from the DOM.
        if (!isEdit && activityId === OTHER) {
          const customNameEl = document.getElementById('sf-custom-activity-name') as HTMLInputElement | null;
          const customImageEl = document.getElementById('sf-custom-activity-image-url') as HTMLInputElement | null;
          const customName = (customNameEl?.value || '').trim();
          if (!customName) {
            alert('Please enter a name for the new activity.');
            throw new Error('missing custom activity name');
          }
          const created = await api.createActivity({
            label: customName,
            category: 'generic',
            unit_label: 'resource',
            image_url: customImageEl?.value?.trim() || null,
          });
          activityId = created.id;
        }

        const body = {
          activity_id: activityId,
          number: Number(values.number),
          label: String(values.label) || null,
          status: String(values.status),
        };
        if (isEdit) await api.updateResource(r!.id, body);
        else await api.createResource(body);
      },
      isEdit
        ? {
            onDelete: async () => {
              await api.deleteResource(r!.id);
              await reloadSetupData();
              await loadAuditLog();
            },
            deleteConfirmMsg: `Delete '${r!.label || 'Resource ' + r!.number}'? This cannot be undone.`,
          }
        : {},
    );

    // Post-render: inject the custom-name + image-upload inputs right after
    // the activity_id select, hidden by default. Toggle visibility based on
    // the dropdown value. We do this here (instead of extending
    // SetupFieldDef) so the generic modal helper stays simple.
    if (!isEdit) {
      const form = document.getElementById('setup-form') as HTMLFormElement | null;
      const sel = form?.querySelector('[name="activity_id"]') as HTMLSelectElement | null;
      if (form && sel) {
        const wrap = document.createElement('div');
        wrap.id = 'sf-custom-activity-wrap';
        wrap.style.display = sel.value === OTHER ? '' : 'none';
        wrap.innerHTML = `
          <div class="form-group">
            <label for="sf-custom-activity-name">New activity name</label>
            <input type="text" id="sf-custom-activity-name" name="custom_activity_name" placeholder="e.g. Axe Throwing">
          </div>
          <div class="form-group">
            <label>Activity image (optional)</label>
            <div style="display:flex; gap:0.5rem; align-items:center;">
              <input type="file" id="sf-custom-activity-file" accept="image/*" style="flex:1;">
              <button type="button" class="btn btn-ghost" id="sf-custom-activity-clear">Clear</button>
            </div>
            <input type="hidden" id="sf-custom-activity-image-url" name="custom_activity_image_url" value="">
            <div class="field-hint" id="sf-custom-activity-image-hint">PNG, JPG, SVG, or WebP. Max 200 KB. Stored inline.</div>
          </div>
        `;
        sel.parentElement?.insertAdjacentElement('afterend', wrap);

        sel.addEventListener('change', () => {
          wrap.style.display = sel.value === OTHER ? '' : 'none';
        });

        const fileEl = wrap.querySelector('#sf-custom-activity-file') as HTMLInputElement;
        const urlEl = wrap.querySelector('#sf-custom-activity-image-url') as HTMLInputElement;
        const hintEl = wrap.querySelector('#sf-custom-activity-image-hint') as HTMLElement;
        const clearBtn = wrap.querySelector('#sf-custom-activity-clear') as HTMLButtonElement;
        clearBtn.addEventListener('click', () => {
          fileEl.value = '';
          urlEl.value = '';
          hintEl.textContent = 'PNG, JPG, SVG, or WebP. Max 200 KB. Stored inline.';
        });
        fileEl.addEventListener('change', () => {
          const file = fileEl.files?.[0];
          if (!file) return;
          if (file.size > 200 * 1024) {
            hintEl.textContent = `Image too large (${Math.round(file.size / 1024)} KB). Max 200 KB.`;
            fileEl.value = '';
            urlEl.value = '';
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            urlEl.value = typeof reader.result === 'string' ? reader.result : '';
            hintEl.textContent = `Loaded ${file.name} (${Math.round(file.size / 1024)} KB).`;
          };
          reader.onerror = () => {
            hintEl.textContent = 'Could not read file.';
          };
          reader.readAsDataURL(file);
        });
      }
    }
  }

  function openAddonModal(a: ApiAddon | null) {
    const isEdit = !!a;
    const slugBase = a?.slug ?? '';
    const fields: SetupFieldDef[] = [
      ...(isEdit ? [] : [{ name: 'slug', label: 'Slug', type: 'text' as const, required: true, placeholder: 'e.g. pizza-slice' }]),
      { name: 'label', label: 'Label', type: 'text', required: true, value: a?.label ?? '' },
      { name: 'description', label: 'Description', type: 'textarea', value: (a as unknown as { description?: string })?.description ?? '' },
      { name: 'price_cents', label: 'Price', type: 'dollars', required: true, value: a?.price_cents ?? 0 },
      {
        name: 'category',
        label: 'Category',
        type: 'select',
        value: a?.category ?? 'food',
        options: [
          { value: 'food', label: 'Food' },
          { value: 'bar', label: 'Bar' },
          { value: 'gear', label: 'Gear' },
          { value: 'upgrade', label: 'Upgrade' },
        ],
      },
      { name: 'per_person', label: 'Per person', type: 'checkbox', value: a?.per_person === 1 },
      {
        name: 'auto_add_per_person',
        label: 'Auto-add one per person (e.g. shoe rental)',
        type: 'checkbox',
        value: (a as unknown as { auto_add_per_person?: number })?.auto_add_per_person === 1,
      },
      { name: 'featured', label: 'Featured', type: 'checkbox', value: (a as unknown as { featured?: number })?.featured === 1 },
      { name: 'inventory', label: 'Inventory (blank = unlimited)', type: 'number', value: (a as unknown as { inventory?: number | null })?.inventory ?? '' },
      { name: 'icon', label: 'Icon (emoji)', type: 'text', value: a?.icon ?? '' },
      { name: 'enabled', label: 'Enabled', type: 'checkbox', value: a?.enabled === 1 },
    ];
    openSetupModal(
      isEdit ? 'Edit Add-On' : 'Add Add-On',
      fields,
      async (values) => {
        const invRaw = values.inventory;
        const body: Record<string, unknown> = {
          label: values.label,
          description: values.description || null,
          price_cents: Number(values.price_cents),
          category: values.category,
          per_person: values.per_person ? 1 : 0,
          auto_add_per_person: values.auto_add_per_person ? 1 : 0,
          featured: values.featured ? 1 : 0,
          inventory: invRaw === '' || invRaw === 0 ? null : Number(invRaw),
          icon: values.icon || null,
          enabled: values.enabled ? 1 : 0,
        };
        if (!isEdit) body.slug = String(values.slug || slugBase || '').trim();
        if (isEdit) await api.updateAddon(a!.id, body);
        else await api.createAddon({ ...body, sort_order: setupAddons.length });
      },
      isEdit
        ? {
            onDelete: async () => {
              await api.deleteAddon(a!.id);
              await reloadSetupData();
              await loadAuditLog();
            },
            deleteConfirmMsg: `Delete '${a!.label}'? This cannot be undone.`,
          }
        : {},
    );
  }

  function openPackageModal(p: ApiPackage | null) {
    const isEdit = !!p;
    const fields: SetupFieldDef[] = [
      ...(isEdit ? [] : [{ name: 'slug', label: 'Slug', type: 'text' as const, required: true }]),
      { name: 'label', label: 'Label', type: 'text', required: true, value: p?.label ?? '' },
      { name: 'description', label: 'Description', type: 'textarea', value: p?.description ?? '' },
      { name: 'price_cents', label: 'Price', type: 'dollars', required: true, value: p?.price_cents ?? 0 },
      { name: 'enabled', label: 'Enabled', type: 'checkbox', value: p?.enabled === 1 },
    ];
    openSetupModal(
      isEdit ? 'Edit Package' : 'Add Package',
      fields,
      async (values) => {
        const body: Record<string, unknown> = {
          label: values.label,
          description: values.description || null,
          price_cents: Number(values.price_cents),
          enabled: values.enabled ? 1 : 0,
        };
        if (!isEdit) {
          body.slug = String(values.slug || '').trim();
          body.included_items = [];
        }
        if (isEdit) await api.updatePackage(p!.id, body);
        else await api.createPackage({ ...body, sort_order: setupPackages.length });
      },
      isEdit
        ? {
            onDelete: async () => {
              await api.deletePackage(p!.id);
              await reloadSetupData();
              await loadAuditLog();
            },
            deleteConfirmMsg: `Delete '${p!.label}'? This cannot be undone.`,
          }
        : {},
    );
  }

  function epochToDateStr(epoch: number | null | undefined): string {
    if (epoch == null) return '';
    const d = new Date(epoch * 1000);
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  function dateStrToEpoch(s: string | null | undefined, edge: 'start' | 'end'): number | null {
    if (!s) return null;
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return null;
    // Local-time start-of-day or end-of-day
    const date = edge === 'start' ? new Date(y, m - 1, d, 0, 0, 0, 0) : new Date(y, m - 1, d, 23, 59, 59, 999);
    return Math.floor(date.getTime() / 1000);
  }

  function openPricingModal(r: PricingRule | null) {
    const isEdit = !!r;
    const fields: SetupFieldDef[] = [
      {
        name: 'activity_id',
        label: 'Activity',
        type: 'select',
        required: true,
        value: r?.activity_id ?? activities[0]?.id ?? '',
        options: activities.map((a) => ({ value: a.id, label: a.label })),
      },
      { name: 'label', label: 'Label', type: 'text', value: r?.label ?? '' },
      {
        name: 'day_of_week',
        label: 'Day of week (blank = any)',
        type: 'select',
        value: r?.day_of_week === null || r?.day_of_week === undefined ? '' : String(r?.day_of_week),
        options: [
          { value: '', label: 'Any day' },
          { value: '0', label: 'Sunday' },
          { value: '1', label: 'Monday' },
          { value: '2', label: 'Tuesday' },
          { value: '3', label: 'Wednesday' },
          { value: '4', label: 'Thursday' },
          { value: '5', label: 'Friday' },
          { value: '6', label: 'Saturday' },
        ],
      },
      { name: 'start_time', label: 'Start time (HH:MM)', type: 'text', value: r?.start_time ?? '' },
      { name: 'end_time', label: 'End time (HH:MM)', type: 'text', value: r?.end_time ?? '' },
      {
        name: 'rate_type',
        label: 'Rate type',
        type: 'select',
        value: r?.rate_type ?? 'per_hour',
        options: [
          { value: 'per_hour', label: 'Per hour' },
          { value: 'per_person_per_hour', label: 'Per person / hour' },
          { value: 'per_person_flat', label: 'Per person flat' },
          { value: 'per_round', label: 'Per round' },
          { value: 'flat', label: 'Flat' },
        ],
      },
      { name: 'rate_cents', label: 'Rate', type: 'dollars', required: true, value: r?.rate_cents ?? 0 },
      {
        name: 'hour_index',
        label: 'Hour (for tiered hourly pricing — blank = applies to all hours)',
        type: 'select',
        value: r?.hour_index == null ? '' : String(r?.hour_index),
        options: [
          { value: '', label: 'Any hour (default)' },
          { value: '1', label: 'Hour 1 only' },
          { value: '2', label: 'Hour 2 only' },
          { value: '3', label: 'Hour 3 only' },
          { value: '4', label: 'Hour 4 only' },
          { value: '5', label: 'Hour 5 only' },
        ],
      },
      {
        name: 'min_party_size',
        label: 'Min party size (optional — use for tiered group rates, e.g. set 4 to discount when 4+ players book together)',
        type: 'number',
        min: 1,
        value: (r as unknown as { min_party_size?: number | null })?.min_party_size ?? '',
      },
      {
        name: 'max_party_size',
        label: 'Max party size (optional — leave blank for unlimited)',
        type: 'number',
        min: 1,
        value: (r as unknown as { max_party_size?: number | null })?.max_party_size ?? '',
      },
      {
        name: 'valid_from',
        label: 'Valid from (optional — use for seasonal pricing, e.g. summer specials June 1 to August 31)',
        type: 'date',
        value: epochToDateStr((r as unknown as { valid_from?: number | null })?.valid_from ?? null),
      },
      {
        name: 'valid_to',
        label: 'Valid until (optional — leave blank for no end date)',
        type: 'date',
        value: epochToDateStr((r as unknown as { valid_to?: number | null })?.valid_to ?? null),
      },
      {
        name: 'event_id',
        label: 'Event (optional) — Apply this rate only during the selected themed event. Leave blank for activity-wide rates.',
        type: 'select',
        value: (r as unknown as { event_id?: string | null })?.event_id ?? '',
        options: [
          { value: '', label: '—' },
          ...setupEvents.map((e) => ({ value: e.id, label: e.name })),
        ],
      },
    ];
    openSetupModal(
      isEdit ? 'Edit Pricing Rule' : 'Add Pricing Rule',
      fields,
      async (values) => {
        const body: Record<string, unknown> = {
          activity_id: values.activity_id,
          label: values.label || null,
          day_of_week: values.day_of_week === '' ? null : Number(values.day_of_week),
          start_time: values.start_time || null,
          end_time: values.end_time || null,
          rate_type: values.rate_type,
          rate_cents: Number(values.rate_cents),
          hour_index: values.hour_index === '' || values.hour_index == null ? null : Number(values.hour_index),
          min_party_size:
            values.min_party_size === '' || values.min_party_size == null || values.min_party_size === 0
              ? null
              : Number(values.min_party_size),
          max_party_size:
            values.max_party_size === '' || values.max_party_size == null || values.max_party_size === 0
              ? null
              : Number(values.max_party_size),
          valid_from: dateStrToEpoch(values.valid_from as string, 'start'),
          valid_to: dateStrToEpoch(values.valid_to as string, 'end'),
          event_id: values.event_id === '' ? null : values.event_id,
        };
        if (isEdit) await api.updatePricingRule(r!.id, body);
        else await api.createPricingRule(body);
      },
      isEdit
        ? {
            onDelete: async () => {
              await api.deletePricingRule(r!.id);
              await reloadSetupData();
              await loadAuditLog();
            },
            deleteConfirmMsg: `Delete pricing rule '${r!.label || r!.rate_type}'? This cannot be undone.`,
          }
        : {},
    );
  }

  async function disableSetupEntity(
    kind: 'resource' | 'addon' | 'package',
    id: string,
  ) {
    try {
      if (kind === 'resource') await api.updateResource(id, { status: 'inactive' });
      else if (kind === 'addon') await api.updateAddon(id, { enabled: 0 });
      else if (kind === 'package') await api.updatePackage(id, { enabled: 0 });
      await reloadSetupData();
      await loadAuditLog();
    } catch (err) {
      const m = err instanceof ApiError ? err.message : 'Disable failed';
      alert(m);
    }
  }

  function deleteSetupEntity(
    kind: 'resource' | 'addon' | 'package' | 'pricing_rule',
    id: string,
    label: string,
  ) {
    const msg = `Delete '${label}'? This cannot be undone.`;
    showSetupConfirm(msg, async () => {
      try {
        if (kind === 'resource') await api.deleteResource(id);
        else if (kind === 'addon') await api.deleteAddon(id);
        else if (kind === 'package') await api.deletePackage(id);
        else if (kind === 'pricing_rule') await api.deletePricingRule(id);
        await reloadSetupData();
        await loadAuditLog();
      } catch (err) {
        const m = err instanceof ApiError ? err.message : 'Delete failed';
        alert(m);
      }
    });
  }

  async function loadAuditLog() {
    const entityFilter = (document.getElementById('audit-entity-filter') as HTMLSelectElement).value;
    const fromRaw = (document.getElementById('audit-from') as HTMLInputElement).value;
    const toRaw = (document.getElementById('audit-to') as HTMLInputElement).value;
    const params: Parameters<typeof api.auditLog>[0] = { limit: 100 };
    if (entityFilter) params.entity_type = entityFilter;
    if (fromRaw) params.from = Math.floor(new Date(fromRaw + 'T00:00:00').getTime() / 1000);
    if (toRaw) params.to = Math.floor(new Date(toRaw + 'T23:59:59').getTime() / 1000);
    try {
      const { entries } = await api.auditLog(params);
      const tbody = document.getElementById('setup-audit-tbody')!;
      if (!entries.length) {
        tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:1.25rem;color:var(--text-muted)">No changes logged yet.</td></tr>';
        return;
      }
      tbody.innerHTML = entries
        .map((entry) => {
          const when = new Date(entry.created_at * 1000).toLocaleString('en-US');
          const who = entry.user_email || entry.user_name || '—';
          const diff = entry.diff || [];
          const diffRows =
            diff.length === 0
              ? `<tr><td>${esc(when)}</td><td>${esc(who)}</td><td>${esc(entry.entity_type || '—')}</td><td>${esc(entry.action)}</td><td>—</td><td>—</td></tr>`
              : diff
                  .map(
                    (d) =>
                      `<tr><td>${esc(when)}</td><td>${esc(who)}</td><td>${esc(entry.entity_type || '—')}</td><td>${esc(entry.action)}</td><td>${esc(d.field)}</td><td>${esc(String(d.from ?? '—'))} → ${esc(String(d.to ?? '—'))}</td></tr>`,
                  )
                  .join('');
          return diffRows;
        })
        .join('');
    } catch (err) {
      console.error('[admin] audit log load failed', err);
    }
  }

  // ---- Helpers ----
  function el(tag: string, cls: string, text: string): HTMLElement {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text) e.textContent = text;
    return e;
  }
  function esc(str: string): string {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }
  function hexWithAlpha(hex: string, alpha: number): string {
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return hex;
    let h = m[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // ---- Wire everything up ----
  function init() {
    buildTimeOptions();
    buildActivityOptions();
    buildAddons();
    buildPackages();

    // Render tenant identity (logo OR name) beside the LaneBooker brand.
    renderHeaderTenant(tenant as Record<string, unknown>);

    // Date nav
    const picker = document.getElementById('date-picker') as HTMLInputElement;
    picker.value = currentDate;
    picker.addEventListener('change', () => {
      currentDate = picker.value;
      void buildGrid();
    });
    document.getElementById('btn-prev-day')!.addEventListener('click', () => {
      currentDate = shiftDate(currentDate, -1);
      picker.value = currentDate;
      void buildGrid();
    });
    document.getElementById('btn-next-day')!.addEventListener('click', () => {
      currentDate = shiftDate(currentDate, 1);
      picker.value = currentDate;
      void buildGrid();
    });
    document.getElementById('btn-today')!.addEventListener('click', () => {
      currentDate = todayStr();
      picker.value = currentDate;
      void buildGrid();
    });

    document.getElementById('btn-new-booking')!.addEventListener('click', () => {
      openNewBooking(null, 10);
    });

    (document.getElementById('f-activity') as HTMLSelectElement).addEventListener('change', (e) => {
      const slug = (e.target as HTMLSelectElement).value;
      buildLanePicker(slug);
      autoSelectShoeRental(slug);
      recalcPrice();
    });
    document.getElementById('f-duration')!.addEventListener('change', recalcPrice);
    document.getElementById('f-people')!.addEventListener('input', recalcPrice);

    // Modal buttons
    document.getElementById('modal-close')!.addEventListener('click', () => closeModal('modal-overlay'));
    document
      .getElementById('btn-cancel-modal')!
      .addEventListener('click', () => closeModal('modal-overlay'));
    document.getElementById('btn-save-booking')!.addEventListener('click', () => void saveBooking());
    document
      .getElementById('btn-delete-booking')!
      .addEventListener('click', () => openModal('confirm-overlay'));
    document.getElementById('btn-mark-paid')!.addEventListener('click', () => void markPaid());
    document
      .getElementById('btn-refund-booking')!
      .addEventListener('click', () => void refundCurrentBooking());
    document.getElementById('btn-rebook')!.addEventListener('click', () => rebookCurrentBooking());
    document.getElementById('btn-print-receipt')!.addEventListener('click', () => window.print());

    // Confirm delete
    document.getElementById('btn-confirm-no')!.addEventListener('click', () =>
      closeModal('confirm-overlay'),
    );
    document.getElementById('btn-confirm-yes')!.addEventListener('click', () => void confirmDelete());

    // Modal overlay close
    document.getElementById('modal-overlay')!.addEventListener('click', (e) => {
      if (e.target === document.getElementById('modal-overlay')) closeModal('modal-overlay');
    });
    document.getElementById('confirm-overlay')!.addEventListener('click', (e) => {
      if (e.target === document.getElementById('confirm-overlay')) closeModal('confirm-overlay');
    });

    // Tabs
    document.getElementById('tab-schedule')!.addEventListener('click', () => switchTab('schedule'));
    document.getElementById('tab-reports')!.addEventListener('click', () => switchTab('reports'));
    document.getElementById('tab-support')!.addEventListener('click', () => switchTab('support'));
    document
      .getElementById('tab-customers')!
      .addEventListener('click', () => switchTab('customers'));
    document.getElementById('tab-setup')!.addEventListener('click', () => switchTab('setup'));
    document
      .getElementById('tab-activity')!
      .addEventListener('click', () => switchTab('activity'));
    document.getElementById('tab-docs')!.addEventListener('click', () => switchTab('docs'));

    // Deep-link: if URL hash is #docs/<slug> on load, jump straight to Docs.
    if (/^#docs(\/|$)/.test(window.location.hash)) {
      switchTab('docs');
    }
    bindActivityPills();
    bindActivitySort();
    document.getElementById('btn-export-bookings-csv')?.addEventListener('click', exportBookingsCSV);
    document.getElementById('btn-export-payments-csv')?.addEventListener('click', exportPaymentsCSV);

    // Customers
    document.getElementById('cust-search')!.addEventListener('input', applyCustomerFilter);
    document.getElementById('cust-filter')!.addEventListener('change', applyCustomerFilter);
    document.getElementById('cust-tag-filter')?.addEventListener('change', applyCustomerFilter);
    document.getElementById('btn-export-csv')!.addEventListener('click', exportCustomerCSV);
    document.querySelectorAll('.cust-table th.sortable').forEach((th) => {
      th.addEventListener('click', () => {
        const col = (th as HTMLElement).dataset.col!;
        if (custSortCol === col) {
          custSortDir = custSortDir === 'asc' ? 'desc' : 'asc';
        } else {
          custSortCol = col;
          custSortDir =
            col === 'lastVisit' || col === 'joined' || col === 'spent' || col === 'visits' || col === 'avgSpend'
              ? 'desc'
              : 'asc';
        }
        applyCustomerFilter();
      });
    });

    document
      .getElementById('cust-modal-close')!
      .addEventListener('click', () => closeModal('cust-modal-overlay'));
    document
      .getElementById('cust-modal-cancel')!
      .addEventListener('click', () => closeModal('cust-modal-overlay'));
    document.getElementById('cust-modal-overlay')!.addEventListener('click', (e) => {
      if (e.target === document.getElementById('cust-modal-overlay')) closeModal('cust-modal-overlay');
    });

    void buildGrid();
  }

  init();
}
